import _ from "lodash";
import React from "react";
import {
  DownloadOutlined,
  AttachmentClipOutlined,
  PlaceholderFileUpArrowOutlined,
  CalendarOutlined,
  DeleteOutlined,
  WarningTriangleOutlined,
  SheetsColored,
  DashboardOutlined,
} from "@toddle-edu/ds-icons";
import { Button, Modal } from "@toddle-edu/ds-web";
import { TIMETABLE_CONSTANTS } from "../constants/stringConstants";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

import { ATTENDANCE_GLOBAL_CONSTANTS } from "modules/Services/ServicesConstants";
import {
  generateRandomId,
  formatDate,
  DAYS_IN_ORDER,
  getDaysNameOrIndexArray,
} from "Utils";

import {
  isDateSameOrBefore,
  isDateSameOrAfter,
  getDateArrBetweenDates,
} from "Utils/DateTimeHelpers";
import moment from "moment";
import ACLStore from "lib/aclStore";
import {
  NON_CYCLE,
  NON_APPLICABLE,
} from "./TimetableModule/TimetableConstants";

//constants
export const GENERAL_INFORMATION = "GENERAL_INFORMATION";
export const MAPPING = "MAPPING";

export const SM_HEIGHT_EVENT = 30;
export const XM_HEIGHT_EVENT = 52;
export const MD_HEIGHT_EVENT = 72;

const PERIODS_COLUMN_WIDTH = 140;
const GUTTER_COLUMN_WIDTH = 0;
const GRID_COLUMN_GAP = 16;
const MIN_COLUMN_WIDTH = 200;
const MAX_COLUMN_WIDTH = 400;
const DEFAULT_TEXT_LENGTH = 10;
const DEFAULT_SEPERATOR = "...";
const MIN_PERIOD_DURATION = 15;

// Declared the function here to avoid circular dependency issue
const convertStringToTestId = str => {
  return _.snakeCase(str);
};

export const isAdminTimeTableEnabled = () => {
  return ACLStore.can("FeatureFlag:AdminTimetable");
};

export const getIsTimetableConfigurationV2 = () => {
  return ACLStore.can("FeatureFlag:TimetableConfigurationV2");
};

export const getIsTimetableV3Enabled = () => {
  return ACLStore.can("FeatureFlag:TimetableV3");
};

export const getIsTimetableSlotSizeIncreaseEnabled = () => {
  return ACLStore.can("FeatureFlag:TimetableSlotSizeIncrease");
};

export const checkTimeZoneConversionEnabled = () => {
  return ACLStore.can("FeatureFlag:GlobalTimezone");
};

export const checkTimetableUploadModesEnabled = () => {
  return ACLStore.can("FeatureFlag:AllowTimetableDeletionOnUpload");
};

export const getUploadOptions = ({ t }) => {
  return [
    {
      label: t("attendance:in_toddle_format"),
      value: "toddleFormat",
      key: "toddleFormat",
      icon: <CalendarOutlined />,
    },
    {
      label: t("attendance:from_asc"),
      value: "ascFormat",
      key: "ascFormat",
      icon: <CalendarOutlined />,
    },
  ];
};

export const getUploadOptionsForTimetableV3 = ({ t }) => {
  return [
    {
      label: t("attendance:in_toddle_format"),
      value: "toddleFormat",
      key: "toddleFormat",
      icon: <SheetsColored />,
    },
    { key: "divider", isDivider: true },
    {
      label: t("attendance:asc_timetables_xml"),
      value: "ascFormat",
      key: "ascFormat",
      icon: <DashboardOutlined />,
    },
    {
      label: t("attendance:edval_csv"),
      value: "edvalFormat",
      key: "edvalFormat",
      icon: <DashboardOutlined />,
    },
    {
      label: "Timetabling solutions (tfx)",
      value: "tfxFormat",
      key: "tfxFormat",
      icon: <DashboardOutlined />,
    },
  ];
};

export const getSideNavigationOptions = t => {
  const isTimetableV3Enabled = getIsTimetableV3Enabled();
  return [
    {
      children: ["SETTING", "TIMETABLE", "HISTORY"],
      key: "root",
    },
    {
      children: [
        "CHECKLIST",
        "PERIODS",
        "BELL_SCHEDULES",
        ...(isTimetableV3Enabled ? ["SPACES", "YARD_DUTIES"] : []),
        "ROUTINES",
        "SCHEDULE_CALENDAR",
      ],
      key: "SETTING",
      parent: "root",
      tier: 2,
      title: t("attendance:timetable_settings"),
    },
    {
      children: [],
      key: "CHECKLIST",
      parent: "SETTING",
      tier: 1,
      title: t("common:checklist"),
    },
    {
      children: [],
      key: "GENERAL_SETTINGS",
      parent: "SETTING",
      tier: 1,
      title: t("common:routine"),
    },
    {
      children: [],
      key: "PERIODS",
      parent: "SETTING",
      tier: 1,
      title: t("scheduler:period_plural"),
    },
    {
      children: [],
      key: "BELL_SCHEDULES",
      parent: "SETTING",
      tier: 1,
      title: t("attendance:bell_schedules"),
    },
    ...(isTimetableV3Enabled
      ? [
          {
            children: [],
            key: "SPACES",
            parent: "SETTING",
            tier: 1,
            title: t("attendance:locations"),
          },
          {
            children: [],
            key: "YARD_DUTIES",
            parent: "SETTING",
            tier: 1,
            title: "Yard Duties",
          },
        ]
      : []),
    {
      children: [],
      key: "ROUTINES",
      parent: "SETTING",
      tier: 1,
      title: t("attendance:routines"),
    },
    {
      children: [],
      key: "SCHEDULE_CALENDAR",
      parent: "SETTING",
      tier: 1,
      title: t("attendance:schedule_calendar"),
    },
    {
      children: ["CLASS_TIMETABLE", "STUDENT_TIMETABLE", "TEACHER_TIMETABLE"],
      key: "TIMETABLE",
      parent: "root",
      tier: 2,
      title: t("attendance:timetables"),
    },
    {
      children: [],
      key: "CLASS_TIMETABLE",
      parent: "TIMETABLE",
      tier: 1,
      title: t("attendance:class_timetable"),
    },
    {
      children: [],
      key: "STUDENT_TIMETABLE",
      parent: "TIMETABLE",
      tier: 1,
      title: t("configuration:student_timetable"),
    },
    {
      children: [],
      key: "TEACHER_TIMETABLE",
      parent: "TIMETABLE",
      tier: 1,
      title: t("attendance:teacher_timetable"),
    },
    {
      children: ["IMPORT_LOG"],
      key: "HISTORY",
      parent: "root",
      tier: 2,
      title: t("attendance:history"),
    },
    {
      children: [],
      key: "IMPORT_LOG",
      parent: "HISTORY",
      tier: 1,
      title: t("attendance:import_log"),
    },
  ];
};

export const getClassesMappingNavigationOptions = ({
  t,
  grades,
  toddleGradesMappingError,
  classes,
}) => {
  const gradesChildren = _.map(grades, ({ id }) => id);
  const gradesOptions = _.map(grades, ({ id, name }) => {
    return {
      children: [],
      key: id,
      parent: "GRADES",
      tier: 1,
      title: (
        <div className={classes.sidebarGradesContainer}>
          <div>{name}</div>
          <div>
            {toddleGradesMappingError[id] > 0 && (
              <WarningTriangleOutlined size="xx-small" variant="critical" />
            )}
          </div>
        </div>
      ),
    };
  });
  return [
    {
      children: ["GRADES"],
      key: "root",
    },
    {
      children: gradesChildren,
      key: "GRADES",
      parent: "root",
      tier: 2,
      title: t("common:grade_plural"),
    },
    ...gradesOptions,
  ];
};

export const getUploadSteps = ({
  t,
  format,
  filledClasURL,
  isUploadingFirstTime,
  isToddleV2TimetableEnabled,
}) => {
  switch (format) {
    case "ascFormat": {
      const ascSteps = [
        {
          stepLabel: "1",
          title: t("attendance:export_xml_asc_title"),
        },
      ];

      if (!isToddleV2TimetableEnabled) {
        ascSteps.push({
          stepLabel: "2",
          title: t("attendance:select_a_routine"),
          subtitle: filledClasURL
            ? t("attendance:choice_to_download_or_retrieve_mapping_file")
            : t("attendance:use_the_xml_file_exported"),
          ctaUIDS: ["SELECT_A_ROUTINE"],
        });
      }

      ascSteps.push({
        stepLabel: isToddleV2TimetableEnabled ? "2" : "3",
        title: t("common:upload_files"),
        subtitle: t("attendance:upload_files_subtitle"),
        ctaUIDS: ["XML_UPLOAD"],
      });

      return ascSteps;
    }

    case "edvalFormat": {
      const edvalSteps = [
        {
          stepLabel: "1",
          title: "Select type of information that you are uploading",
          subtitle: "",
          ctaUIDS: ["UPLOAD_TYPE"],
        },
      ];

      if (!isToddleV2TimetableEnabled) {
        edvalSteps.push({
          stepLabel: "2",
          title: t("attendance:select_a_routine"),
          subtitle: t(
            "attendance:select_routine_for_which_you_want_to_import_tt"
          ),
          ctaUIDS: ["SELECT_A_ROUTINE"],
        });
      }

      edvalSteps.push({
        stepLabel: isToddleV2TimetableEnabled ? "2" : "3",
        title: t("attendance:upload_timetable_file_downloaded_from_edval"),
        subtitle: "",
        ctaUIDS: ["CSV_UPLOAD"],
      });

      return edvalSteps;
    }

    case "tfxFormat": {
      const tfxSteps = [];

      if (!isToddleV2TimetableEnabled) {
        tfxSteps.push({
          stepLabel: "1",
          title: t("attendance:select_a_routine"),
          subtitle: t(
            "attendance:select_routine_for_which_you_want_to_import_tt"
          ),
          ctaUIDS: ["SELECT_A_ROUTINE"],
        });
      }

      tfxSteps.push({
        stepLabel: isToddleV2TimetableEnabled ? "1" : "2",
        title: "Upload timetable file downloaded from Timetabling solutions",
        subtitle: "",
        ctaUIDS: ["TFX_UPLOAD"],
      });

      return tfxSteps;
    }

    case "toddleFormat":
      return [
        {
          stepLabel: "1",
          title: t("attendance:download_toddle_template"),
          subtitle: isUploadingFirstTime
            ? t("attendance:download_and_populate_template_first_time")
            : t("attendance:choice_to_download_or_retrive_template"),
          ctaUIDS: ["NEW_TIMETABLE_TEMPLATE", "DOWNLORD_OLD_TIMETABLE"],
        },
        {
          stepLabel: "2",
          title: isUploadingFirstTime
            ? t("attendance:upload_filled_template_file")
            : t("attendance:upload_timetable"),
          subtitle: isUploadingFirstTime
            ? ""
            : t("attendance:read_instructions_in_template"),
          ctaUIDS: ["TIMETABLE_UPLOAD"],
        },
      ];
  }
};

export const getCTAMappings = ({ t, isToddleV2TimetableEnabled }) => ({
  EMPTY_CLASS_FILE: {
    label: t("attendance:new_mapping_file"),
    icon: <DownloadOutlined />,
    buttonType: TIMETABLE_CONSTANTS.DOWNLOAD_BUTTON,
  },
  FILLED_CLASS_FILE: {
    label: t("attendance:last_mapping_file"),
    icon: <DownloadOutlined />,
    buttonType: TIMETABLE_CONSTANTS.DOWNLOAD_BUTTON,
  },
  XML_UPLOAD: {
    label: t("attendance:xml_export_from_asc"),
    icon: <AttachmentClipOutlined />,
    buttonType: TIMETABLE_CONSTANTS.UPLOAD_BUTTON,
    allowedFileTypes: [".xml"],
  },
  CSV_UPLOAD: {
    label: t("attendance:upload_csv"),
    icon: <PlaceholderFileUpArrowOutlined />,
    buttonType: TIMETABLE_CONSTANTS.UPLOAD_BUTTON,
    allowedFileTypes: [".csv"],
  },
  TFX_UPLOAD: {
    label: "Upload tfx",
    icon: <PlaceholderFileUpArrowOutlined />,
    buttonType: TIMETABLE_CONSTANTS.UPLOAD_BUTTON,
    allowedFileTypes: [".tfx"],
  },
  SELECT_A_ROUTINE: {
    label: isToddleV2TimetableEnabled
      ? "Select a Timetable"
      : t("attendance:select_a_routine"),
    buttonType: TIMETABLE_CONSTANTS.DROPDOWN_BUTTON,
  },
  TIMETABLE_UPLOAD: {
    label: t("attendance:mapping_file"),
    icon: <AttachmentClipOutlined />,
    buttonType: TIMETABLE_CONSTANTS.UPLOAD_BUTTON,
    allowedFileTypes: [".xlsx"],
  },
  UPLOAD_TYPE: {
    buttonType: TIMETABLE_CONSTANTS.RADIO_BUTTON,
  },
});

export const getToddleFormatButtonMappings = ({ t }) => ({
  NEW_TIMETABLE_TEMPLATE: {
    label: t("attendance:new_timetable_template"),
    icon: <DownloadOutlined />,
    buttonType: TIMETABLE_CONSTANTS.DOWNLOAD_BUTTON,
  },
  DOWNLORD_OLD_TIMETABLE: {
    label: t("attendance:download_old_timetables"),
    icon: <DownloadOutlined />,
    buttonType: TIMETABLE_CONSTANTS.DROPDOWN_BUTTON,
  },
  TIMETABLE_UPLOAD: {
    label: t("attendance:upload_timetable"),
    icon: <AttachmentClipOutlined />,
    buttonType: TIMETABLE_CONSTANTS.UPLOAD_BUTTON,
    allowedFileTypes: [".xlsx"],
  },
});

export const getErrorTableColumnList = ({
  uploadStatus,
  lastOpenedModal,
  errorData,
  t,
}) => {
  const isErrorContainsPage = _.get(_.head(errorData), "page", null);
  const pageColumn = isErrorContainsPage
    ? [
        {
          alignment: "center",
          key: "page",
          value: t("common:page"),
        },
      ]
    : [];

  const rowColumn =
    lastOpenedModal === "toddleFormat" && uploadStatus === "SOFT_ERROR"
      ? [
          {
            alignment: "center",
            key: "row",
            value: t("common:row"),
          },
        ]
      : [];

  return [
    ...pageColumn,
    ...rowColumn,
    {
      alignment: "left",
      key: "error",
      value: t("common:error_description"),
    },
  ];
};

export const getTimetableFileUploadVariables = ({
  isEdvalUpload,
  isAscUpload,
  attachmentObj,
  selectedRoutine,
  serviceDataUrl,
  isAscFinalStep,
  isEdvalFinalStep,
  isTfxUpload,
  isTfxFinalStep,
  selectedUploadType,
  isToddleV2TimetableEnabled,
}) => {
  if (isEdvalUpload) {
    return isEdvalFinalStep
      ? {
          service: "EDVAL",
          serviceDataUrl,
        }
      : {
          service: "EDVAL",
          ...(isToddleV2TimetableEnabled
            ? { timetableId: selectedRoutine }
            : { routineId: selectedRoutine }),
          files: [
            {
              fileUrl: _.get(attachmentObj, "CSV_UPLOAD.url"),
              type: selectedUploadType,
            },
          ],
        };
  } else if (isTfxUpload) {
    return isTfxFinalStep
      ? {
          service: "TIMETABLING_SOLUTIONS",
          serviceDataUrl,
        }
      : {
          service: "TIMETABLING_SOLUTIONS",
          ...(isToddleV2TimetableEnabled
            ? { timetableId: selectedRoutine }
            : { routineId: selectedRoutine }),
          files: [
            {
              fileUrl: _.get(attachmentObj, "TFX_UPLOAD.url"),
              type: "REGULAR",
            },
          ],
        };
  } else if (isAscUpload) {
    return isAscFinalStep
      ? {
          service: "ASC",
          serviceDataUrl,
        }
      : {
          service: "ASC",
          xmlUrl: _.get(attachmentObj, "XML_UPLOAD.url"),
          [isToddleV2TimetableEnabled
            ? "timetableId"
            : "routineId"]: selectedRoutine,
          fileName: _.get(attachmentObj, "XML_UPLOAD.name"),
        };
  } else {
    return {
      service: "UPLOAD_TEMPLATE",
      serviceDataUrl: _.get(attachmentObj, "TIMETABLE_UPLOAD.url"),
    };
  }
};

export const getBellScheduleTableHeaders = ({ t }) => {
  return [
    {
      key: "name",
      value: t("common:name"),
    },
    {
      key: "noOfPeriods",
      value: t("attendance:no_of_periods"),
      style: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      key: "actions",
      value: "",
    },
  ];
};

export const getFormattedPeriodList = ({
  periods,
  defaultStartTime,
  defaultEndTime,
}) => {
  return !_.isEmpty(periods)
    ? _.reduce(
        periods,
        (result, { period, startTime, endTime }) => {
          const randomPeriodId = generateRandomId();
          return [
            ...result,
            { [randomPeriodId]: { period, startTime, endTime } },
          ];
        },
        []
      )
    : [
        {
          [generateRandomId()]: {
            period: null,
            startTime: defaultStartTime,
            endTime: defaultEndTime,
            isNew: true,
          },
        },
      ];
};

export const getPeriodOptionsForBellSchedule = ({ periods }) => {
  return _.map(periods, ({ id, label }) => {
    return {
      value: id,
      label,
      customTestId: `timetable-bellSchedule-bellScheduleTableItem-modal-table-period-select-item-${label}`,
    };
  });
};

export const updateTimeInDateObj = ({ date, time }) => {
  const [hour = "00", minute = "00", second = "00"] = _.split(time, ":");
  return moment(date).clone().hour(hour).minute(minute).second(second);
};

export const getAdjustedTimesAndDates = (
  enrolledPeriodDate,
  originalStartTime,
  originalEndTime,
  getUserTime
) => {
  const userStartTime = enrolledPeriodDate + " " + originalStartTime;
  const userEndTime = enrolledPeriodDate + " " + originalEndTime;

  const startTimeInUserZone = getUserTime({ time: userStartTime });
  const endTimeInUserZone = getUserTime({ time: userEndTime });

  const startTime = moment(startTimeInUserZone).format("HH:mm:ss");
  const endTime = moment(endTimeInUserZone).format("HH:mm:ss");

  const startAdjustedDate = moment(startTimeInUserZone).format("YYYY-MM-DD");
  const endAdjustedDate = moment(endTimeInUserZone).format("YYYY-MM-DD");

  return {
    startTime,
    endTime,
    startAdjustedDate,
    endAdjustedDate,
  };
};

export const getAdjustedPeriodEndTime = ({
  periodStartTime,
  periodEndTime,
}) => {
  if (
    moment(periodEndTime).diff(moment(periodStartTime), "minutes") <
    MIN_PERIOD_DURATION
  ) {
    return moment(periodStartTime).add(MIN_PERIOD_DURATION, "minutes");
  }
  return periodEndTime;
};

export const getTimetableCalendarEvents = ({
  enrolledPeriods = [],
  selectedWeek,
  breakPeriodSlots = [],
  getUserTime,
}) => {
  const isTimeZoneConversionEnabled = checkTimeZoneConversionEnabled();
  const startDate = _.get(selectedWeek, "weekStartDate");
  const endDate = _.get(selectedWeek, "weekEndDate");

  const events = [];
  const allEnrolledSlots = [...enrolledPeriods, ...breakPeriodSlots];

  for (
    let date = moment(startDate).subtract(1, "day");
    date.isSameOrBefore(moment(endDate).add(1, "day"), "day");
    date.add(1, "day")
  ) {
    _.forEach(allEnrolledSlots, enrolledPeriod => {
      const {
        date: enrolledPeriodDate,
        period: { id: periodId },
        startTime: originalStartTime,
        endTime: originalEndTime,
        isSelected,
      } = enrolledPeriod;

      const {
        startTime,
        endTime,
        startAdjustedDate,
        endAdjustedDate,
      } = getAdjustedTimesAndDates(
        enrolledPeriodDate,
        originalStartTime,
        originalEndTime,
        getUserTime
      );

      const formattedDay = formatDate({
        date,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      });

      if (
        isSelected &&
        startTime &&
        endTime &&
        moment(formattedDay).isSame(moment(startAdjustedDate), "day")
      ) {
        const periodStartTime = updateTimeInDateObj({
          date: isTimeZoneConversionEnabled
            ? startAdjustedDate
            : enrolledPeriodDate,
          time: isTimeZoneConversionEnabled ? startTime : originalStartTime,
        });
        let periodEndTime = updateTimeInDateObj({
          date: isTimeZoneConversionEnabled
            ? endAdjustedDate
            : enrolledPeriodDate,
          time: isTimeZoneConversionEnabled ? endTime : originalEndTime,
        });
        if (getIsTimetableSlotSizeIncreaseEnabled()) {
          periodEndTime = getAdjustedPeriodEndTime({
            periodStartTime,
            periodEndTime,
          });
        }
        events.push({
          id: `PERIOD_${periodId}_${enrolledPeriodDate}`,
          periodDetails: enrolledPeriod,
          start: periodStartTime.toDate(),
          end: periodEndTime.toDate(),
          itemType: "PERIOD",
        });
      }
    });
  }
  return events;
};

/**
 * @param {object} groupedPeriod - array of grouped periods with same groupKey
 * @param {string} type - abbreviation or label
 * @returns for abbr => "P1, P2 & P3" or "P1 & P2" or "P1", for label => "Period 1, Period 2 & Period 3" or "Period 1 & Period 2" or "Period 1"
 */
export const getFormattedGroupedPeriodLabel = ({
  groupedPeriod,
  type = "abbreviation",
}) => {
  if (_.isEmpty(groupedPeriod)) {
    return "";
  }

  const allGroupedPeriodsAbbr = _.map(groupedPeriod, period =>
    _.get(period, type)
  );

  if (_.size(allGroupedPeriodsAbbr) === 1) {
    return allGroupedPeriodsAbbr[0];
  } else if (_.size(allGroupedPeriodsAbbr) === 2) {
    return _.join(allGroupedPeriodsAbbr, " & ");
  } else {
    const lastPeriodAbbr = allGroupedPeriodsAbbr.pop();
    return _.join(allGroupedPeriodsAbbr, ", ") + " & " + lastPeriodAbbr;
  }
};

export const getMergedTimetableSlotsByGroupKey = ({ timetableSlots }) => {
  // Filter objects with and without groupKey
  const slotsWithGroupKey = _.filter(timetableSlots, slot => slot.groupKey);
  const slotsWithoutGroupKey = _.filter(timetableSlots, slot => !slot.groupKey);

  // Group by 'groupKey' for objects that have it
  const groupedSlots = _.groupBy(slotsWithGroupKey, "groupKey");

  // Transform the grouped data
  const mergedSlots = _.map(groupedSlots, group => {
    const [firstSlot] = group;
    const groupedPeriod = _.map(group, slot => _.get(slot, "period"));
    const startTime = moment
      .min(_.map(group, slot => moment(slot.startTime, "HH:mm:ss")))
      .format("HH:mm:ss");
    const endTime = moment
      .max(_.map(group, slot => moment(slot.endTime, "HH:mm:ss")))
      .format("HH:mm:ss");

    return {
      ...firstSlot,
      startTime,
      endTime,
      groupedPeriod,
    };
  });

  // Combine the merged and unmerged slots
  return [...mergedSlots, ...slotsWithoutGroupKey];
};

export const getTeacherTimetableCalendarEvents = ({
  enrolledPeriods = [],
  selectedWeek,
  breakPeriodSlots = [],
  yardDuties = [],
  getUserTime,
}) => {
  const isTimeZoneConversionEnabled = checkTimeZoneConversionEnabled();
  const startDate = _.get(selectedWeek, "weekStartDate");
  const endDate = _.get(selectedWeek, "weekEndDate");

  // merge the timetable slots by groupKey
  const mergedTimetableSlotsByGroupKey = getMergedTimetableSlotsByGroupKey({
    timetableSlots: enrolledPeriods,
  });

  const events = [];
  const allEnrolledSlots = [
    ...mergedTimetableSlotsByGroupKey,
    ...breakPeriodSlots,
    ...yardDuties,
  ];

  for (
    let date = moment(startDate).subtract(1, "day");
    date.isSameOrBefore(moment(endDate).add(1, "day"), "day");
    date.add(1, "day")
  ) {
    _.forEach(allEnrolledSlots, enrolledPeriod => {
      const {
        date: enrolledPeriodDate,
        period: { id: periodId },
        startTime: originalStartTime,
        endTime: originalEndTime,
        isSelected,
      } = enrolledPeriod;

      const {
        startTime,
        endTime,
        startAdjustedDate,
        endAdjustedDate,
      } = getAdjustedTimesAndDates(
        enrolledPeriodDate,
        originalStartTime,
        originalEndTime,
        getUserTime
      );

      const formattedDay = formatDate({
        date,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      });

      if (
        isSelected &&
        startTime &&
        endTime &&
        moment(formattedDay).isSame(moment(startAdjustedDate), "day")
      ) {
        const periodStartTime = updateTimeInDateObj({
          date: isTimeZoneConversionEnabled
            ? startAdjustedDate
            : enrolledPeriodDate,
          time: isTimeZoneConversionEnabled ? startTime : originalStartTime,
        });
        let periodEndTime = updateTimeInDateObj({
          date: isTimeZoneConversionEnabled
            ? endAdjustedDate
            : enrolledPeriodDate,
          time: isTimeZoneConversionEnabled ? endTime : originalEndTime,
        });
        if (getIsTimetableSlotSizeIncreaseEnabled()) {
          periodEndTime = getAdjustedPeriodEndTime({
            periodStartTime,
            periodEndTime,
          });
        }
        events.push({
          id: `PERIOD_${periodId}_${enrolledPeriodDate}`,
          periodDetails: enrolledPeriod,
          start: periodStartTime.toDate(),
          end: periodEndTime.toDate(),
          itemType: "PERIOD",
        });
      }
    });
  }

  return events;
};

const getBreakPeriodsForDates = ({ rotationDays }) => {
  const breakPeriodsSlots = [];

  _.forEach(_.keys(rotationDays), date => {
    const allPeriods = _.get(
      rotationDays,
      `${date}.bellSchedule.periodsEdge.periods`,
      []
    );

    const breakPeriods = _.filter(
      allPeriods,
      periodItem => _.get(periodItem, "period.type", "") === "BREAK"
    );

    _.forEach(breakPeriods, period =>
      breakPeriodsSlots.push({ date, ...period, isSelected: true })
    );
  });
  return breakPeriodsSlots;
};

export const getBreakPeriodsForDatesMemoized = _.memoize(
  params => getBreakPeriodsForDates(params),
  params => JSON.stringify(params)
);

export const getClassTimetableCalendarEvents = ({
  coursePeriods,
  selectedWeek,
  getUserTime,
}) => {
  const isTimeZoneConversionEnabled = checkTimeZoneConversionEnabled();
  const startDate = _.get(selectedWeek, "weekStartDate");
  const endDate = _.get(selectedWeek, "weekEndDate");

  const events = [];
  for (
    let date = moment(startDate).subtract(1, "day");
    date.isSameOrBefore(moment(endDate).add(1, "day"), "day");
    date.add(1, "day")
  ) {
    _.forEach(coursePeriods, enrolledPeriod => {
      const {
        date: enrolledPeriodDate,
        period: { id: periodId },
        startTime: originalStartTime,
        endTime: originalEndTime,
        isSelected,
      } = enrolledPeriod;

      const {
        startTime,
        endTime,
        startAdjustedDate,
        endAdjustedDate,
      } = getAdjustedTimesAndDates(
        enrolledPeriodDate,
        originalStartTime,
        originalEndTime,
        getUserTime
      );

      const formattedDay = formatDate({
        date,
        format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
      });

      if (
        isSelected &&
        startTime &&
        endTime &&
        moment(formattedDay).isSame(moment(startAdjustedDate), "day")
      ) {
        const periodStartTime = updateTimeInDateObj({
          date: isTimeZoneConversionEnabled
            ? startAdjustedDate
            : enrolledPeriodDate,
          time: isTimeZoneConversionEnabled ? startTime : originalStartTime,
        });
        let periodEndTime = updateTimeInDateObj({
          date: isTimeZoneConversionEnabled
            ? endAdjustedDate
            : enrolledPeriodDate,
          time: isTimeZoneConversionEnabled ? endTime : originalEndTime,
        });
        if (getIsTimetableSlotSizeIncreaseEnabled()) {
          periodEndTime = getAdjustedPeriodEndTime({
            periodStartTime,
            periodEndTime,
          });
        }
        events.push({
          id: `PERIOD_${periodId}_${enrolledPeriodDate}`,
          date: enrolledPeriodDate,
          periodDetails: enrolledPeriod,
          start: periodStartTime.toDate(),
          end: periodEndTime.toDate(),
          itemType: "PERIOD",
        });
      }
    });
  }

  return events;
};

export const getClassTimetableEventsForSync = ({ timetableData }) => {
  const timetableSlots = timetableData.timetable;

  const events = [];
  const scheduleIndexMap = {};
  _.forEach(
    _.get(timetableData, ["routine", "schedule"], []),
    ({ rotationDay }, index) => {
      scheduleIndexMap[_.get(rotationDay, "serviceId", -1)] = index;
    }
  );

  _.forEach(timetableSlots, slot => {
    const { startTime, endTime, date: stringDate } = slot;

    const date = moment(stringDate);

    const randomSlotId = generateRandomId();

    const formattedDay = formatDate({
      date,
      format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
    });
    const periodStartTime = updateTimeInDateObj({
      date,
      time: startTime,
    });
    const periodEndTime = updateTimeInDateObj({ date, time: endTime });

    events.push({
      id: `PERIOD_${randomSlotId}_${formattedDay}`,
      periodDetails: slot,
      start: periodStartTime.toDate(),
      end: periodEndTime.toDate(),
      itemType: "PERIOD",
    });
  });

  return events;
};

export const getTimetableCalendarHolidayEvents = ({ holidayEvents }) => {
  const events = [];

  _.forEach(holidayEvents, holiday => {
    const { id, start, end } = holiday;
    events.push({
      id: `HOLIDAY_${id}`,
      allDay: true,
      holidayDetails: holiday,
      start: moment(start, "YYYY-MM-DD").toDate(),
      end: moment(end, "YYYY-MM-DD").add(1, "days").toDate(),
      itemType: "HOLIDAY",
    });
  });

  return events;
};

export const findAllDayTimetableEvent = ({
  holidayEvents,
  startDate,
  endDate,
}) => {
  return _.find(holidayEvents, ({ start, end }) => {
    return (
      isDateSameOrBefore(start, endDate) && isDateSameOrAfter(end, startDate)
    );
  });
};

export const getEarliestPeriodStartTime = ({ slots }) => {
  const startTimes = _.map(slots, ({ startTime }) =>
    moment(startTime, "HH:mm:ss")
  );
  return moment.min(startTimes);
};

export const getFormattedWeekdaysBellScheduleMapping = ({
  operationalDays,
  bellSchedules,
}) => {
  const routineWeekdaysScheduleMapping = _.reduce(
    operationalDays,
    (result, operationalDay) => {
      const definedScheduleForWeekday = _.find(
        bellSchedules,
        ({ weekday }) => operationalDay === weekday
      );

      const randomScheduleId = generateRandomId();
      const { id, weekday, bellSchedule } = definedScheduleForWeekday || {};
      const weekdayName = DAYS_IN_ORDER[weekday];

      const operationalDayValue = DAYS_IN_ORDER[operationalDay];
      const undefinedWeekday = {
        id: operationalDayValue,
        label: operationalDayValue,
        weekdayNumber: operationalDay,
      };

      return definedScheduleForWeekday
        ? [
            ...result,
            {
              [id]: {
                dayType: "OPERATIONAL_DAY",
                weekday: {
                  id: weekdayName,
                  label: weekdayName,
                  weekdayNumber: weekday,
                },

                bellSchedule: {
                  id: _.get(bellSchedule, "id"),
                  label: _.get(bellSchedule, "label"),
                },
              },
            },
          ]
        : [
            ...result,
            {
              [randomScheduleId]: {
                dayType: "OPERATIONAL_DAY",
                weekday: undefinedWeekday,
                bellSchedule: null,
                isNew: true,
              },
            },
          ];
    },
    []
  );

  return routineWeekdaysScheduleMapping;
};

// TODO: localise text
export const getDayPatternTableHeaders = ({ t }) => {
  const commonColumnStyle = {
    width: "160px",
  };

  return [
    {
      key: "WEEKDAYS",
      value: t("common:weekdays"),
      style: commonColumnStyle,
    },
    {
      key: "RELATION",

      value: t("common:relation"),
      style: commonColumnStyle,
    },
    {
      key: "ROTATION_DAYS",
      value: t("attendance:rotation_days"),
      style: commonColumnStyle,
    },
    {
      key: "ACTIONS",
    },
  ];
};

export const getFormattedDayPatternLabel = ({ rotationDays }) => {
  const rotationDaysLabel = _.map(rotationDays, rotationDay =>
    _.get(rotationDay, "label")
  );
  return _.join(rotationDaysLabel, ", ");
};

export const getDefaultDayPatternCycle = ({ dayOptions }) => {
  return _.reduce(
    dayOptions,
    (result, rotationDay) => {
      const randomPatternId = generateRandomId();
      return [...result, { [randomPatternId]: rotationDay }];
    },
    []
  );
};

export const getDayPatternRotationDaysOptions = ({ t, rotationDays }) => {
  const dayPatternRotationDaysOptions = _.map(rotationDays, rotationDay => {
    const { id, label } = rotationDay;
    return { key: id, label };
  });
  dayPatternRotationDaysOptions.push({
    key: "DIVIDER",
    isDivider: true,
  });
  dayPatternRotationDaysOptions.push({
    key: "DELETE",
    icon: <DeleteOutlined />,
    label: t("attendance:delete_cell"),
    isDestructive: true,
  });

  return dayPatternRotationDaysOptions;
};

export const getDayPatternStartingCycleFromOptions = ({ dayPatterns }) => {
  return _.map(dayPatterns, dayPattern => {
    const dayPatternId = _.head(_.keys(dayPattern));
    const dayPatternValue = dayPattern[dayPatternId];
    const { id, label, isStartDay } = dayPatternValue;
    const formattedLabel = convertStringToTestId(label);

    return {
      value: dayPatternId,
      id,
      label,
      isStartDay,
      customTestId: `timetable-routines-defineCycle-startingCycle-select-item-${formattedLabel}`,
    };
  });
};

const DeleteSelectOptionModal = ({
  isOpen,
  onModalClose,
  onModalConfirm,
  deleteOptionState,
  t,
}) => {
  const { action, selectedOption } = deleteOptionState;

  const buttonStyles = {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  };

  return (
    <Modal height="fit-content" hasOverlay={true} isOpen={isOpen} width="553px">
      <Modal.Header
        title={
          action === "clear"
            ? t("attendance:delete_all_grades_modal_title")
            : t("attendance:delete_grade_modal_title", {
                grade: selectedOption?.label,
              })
        }
        isClosable={false}
      />
      <Modal.Body>
        <div>
          {action === "clear"
            ? t("attendance:delete_all_grades_modal_body_text")
            : t("attendance:delete_grade_modal_body_text", {
                grade: selectedOption?.label,
              })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div style={buttonStyles}>
          <Button
            onClick={onModalClose}
            variant={"outlined-subtle"}
            size={"large"}
            aria-label={`Cancel ${selectedOption?.label} deletion`}
          >
            {t("common:cancel")}
          </Button>
          <Button
            onClick={onModalConfirm}
            variant={"primary"}
            size={"large"}
            aria-label={`Remove ${selectedOption?.label} from grades`}
          >
            {t("common:remove")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const getDayOptions = ({
  date,
  isRoutineBasedOnOperationalCycle,
  rotationDays,
  upcomingRotationDay,
  countHolidayAsRotationDay,
  selectedDay,
  isHoliday,
  rotationDay,
  routineLabel,
  isRoutineWeekdayDependent,
  t,
}) => {
  if (isRoutineBasedOnOperationalCycle) {
    const weekDay = moment(date).format("ddd");
    return [
      {
        key: weekDay,
        label: weekDay,
      },
    ];
  }
  const routineTestId = convertStringToTestId(routineLabel);

  let options = [];
  options = _.map(rotationDays, ({ id, label }) => {
    const formattedLabel = convertStringToTestId(label);
    return {
      key: generateRandomId(),
      id,
      label,
      customTestId: `timetable-scheduleCalender-${routineTestId}-bellSchedule-dropdown-item-${formattedLabel}`,
    };
  });

  options.push({
    isDivider: true,
  });
  options.push({
    id: NON_CYCLE,
    key: NON_CYCLE,
    label: t("attendance:non_cycle_day"),
    subText: isRoutineWeekdayDependent
      ? t("attendance:future_cycle_will_not_affected")
      : t("attendance:cycle_push_one_day"),
    customTestId: `timetable-scheduleCalender-${routineTestId}-bellSchedule-dropdown-item-non-cycle-day`,
  });

  return options;
};

export const getDayOptionsMemoized = _.memoize(
  params => getDayOptions(params),
  params => JSON.stringify(params)
);

const getBellScheduleOptions = ({ bellSchedules, routineLabel }) => {
  const routineTestId = convertStringToTestId(routineLabel);
  const options = _.reduce(
    bellSchedules,
    (reducedList, { id, label }) => {
      const formattedLabel = convertStringToTestId(label);
      reducedList.push({
        key: id,
        label,
        customTestId: `timetable-scheduleCalender-${routineTestId}-bellSchedule-dropdown-item-${formattedLabel}`,
      });
      return reducedList;
    },
    []
  );
  options.push({
    key: NON_APPLICABLE,
    label: "Not applicable",
    customTestId: `timetable-scheduleCalender-${routineTestId}-bellSchedule-dropdown-item-not-applicable`,
  });
  return options;
};

export const getBellScheduleOptionsMemoized = _.memoize(
  params => getBellScheduleOptions(params),
  params => JSON.stringify(params)
);

export const getRoutineBasicInfoTemplate = ({ t }) => {
  return {
    basicInfo: {
      steps: ["label", "applicable_for", "days_of_cycle", "validity_period"],
      step_list: {
        label: {
          label: t("attendance:name_of_routine"),
          isSectionHeader: true,
          fields: ["label"],
        },
        applicable_for: {
          label: t("common:applicable_for"),
          subText: t("attendance:applicable_grade_description"),
          fields: ["grades"],
          isSectionHeader: true,
        },
        days_of_cycle: {
          label: t("attendance:days_of_cycle"),
          subText: t("attendance:applicable_grades_in_routine"),
          fields: ["rotationDays", "nameOfDays"],
          isSectionHeader: true,
        },
        operational_weekdays: {
          label: t("attendance:operational_weekdays"),
          subText: t("attendance:specify_weekdays_in_routine"),
          fields: ["opWeekdays"],
          isSectionHeader: true,
        },
        validity_period: {
          label: t("attendance:validity_period"),
          subText: t("attendance:indicate_duration_for_routine"),
          fields: ["dateDuration"],
          isSectionHeader: true,
        },
      },
      field_list: {
        label: {
          config: {
            isRequired: true,
            placeholder: `${t("common:title")}*`,
          },
          type: "InputField",
          uiComponent: "FieldComponent",
        },
        grades: {
          config: {
            isRequired: true,
            label: t("common:select_grades_placeholder"),
            placeholder: t("common:select_grades_placeholder"),
            fieldTypeConfig: {
              multi: true,
              deleteModal: {
                showModalOnOptionDelete: true,
                DeleteSelectOptionModal,
              },
            },
          },

          type: "SelectDropdown",
          uiComponent: "FieldComponent",
        },
        dateDuration: {
          config: {
            isMappedWithDetailsFields: true,
            mappedDetailsFields: ["startDate", "endDate"],
            isRequiredToCreateUnit: true,
            fieldTypeConfig: {
              startDateLabel: t("common:starting_from"),
              endDateLabel: t("common:ending_on"),
            },
            isRequired: true,
          },
          type: "FormDateDurationSelector",
          uiComponent: "FieldComponent",
        },
        opWeekdays: {
          uiComponent: "Custom",
        },
        rotationDays: {
          config: {
            isRequired: true,
            label: t("attendance:number_of_days"),
            fieldTypeConfig: {
              inputType: "number",
              min: 1,
            },
          },
          type: "InputField",
          uiComponent: "FieldComponent",
        },
        nameOfDays: {
          config: {
            label: t("attendance:name_of_days"),
            fieldTypeConfig: {
              labelContainerStyle: {
                width: "unset",
              },
            },
          },
          uiComponent: "Custom",
        },
      },
    },
  };
};

export const updateRoutineBasicDetailsTemplate = (template, field, params) => {
  switch (field) {
    case "grades": {
      template["options"] = params["grades"];
      return template;
    }
    default:
      return template;
  }
};

export const getTemplateFieldValue = ({ field, data }) => {
  switch (field) {
    default:
      return _.get(data, field, "");
  }
};

export const getOperationDaysValue = ({ operationalDays }) => {
  const operationalDaysValue = getDaysNameOrIndexArray({
    array: operationalDays,
  });

  return _.map(operationalDaysValue, (day, index) => ({
    label: day,
    value: `${day}_${index}`,
  }));
};

export const getDayNameByPattern = ({
  pattern,
  basicDetails,
  selectedDays,
  operationalDays,
  isModeChanged,
}) => {
  const value = getTemplateFieldValue({
    field: "nameOfDays",
    data: basicDetails,
  });

  switch (pattern) {
    case ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS:
      return {
        pattern,
        days: getOperationDaysValue({ operationalDays }),
      };
    case ATTENDANCE_GLOBAL_CONSTANTS.ROTATION_CYCLE:
      return {
        pattern,
        days: !isModeChanged
          ? _.times(selectedDays, day => {
              const addedDay = value[day];
              return {
                label: _.get(addedDay, "label", `Day ${day + 1}`),
                value: _.get(addedDay, "value", day),
                abbreviation: _.get(addedDay, "abbreviation", `${day + 1}`),
              };
            })
          : _.times(selectedDays, day => {
              return {
                label: `Day ${day + 1}`,
                value: `${day}`,
                abbreviation: `${day + 1}`,
              };
            }),
      };
  }
};

//this function is used in setting basic details of routine
export const setRoutineBasicDetails = ({
  currentAcademicYear,
  operationalDays,
  mode,
  routineDetails,
}) => {
  const operationalDaysValue = getOperationDaysValue({ operationalDays });

  if (mode === "EDIT") {
    const {
      label = "",
      routineMode,
      rotationDays,
      startDate,
      endDate,
      grades = [],
    } = routineDetails;

    const nameOfOperationalDays = _.map(rotationDays, (day, index) => ({
      label: day.label,
      value: day.id,
      abbreviation: _.get(day, "abbreviation", index + 1),
    }));

    const isOperationalDays =
      routineMode === ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS;

    return {
      label,
      grades: _.map(grades, data => data.id),
      routineMode,
      rotationDays: isOperationalDays
        ? _.size(operationalDaysValue)
        : _.size(rotationDays),
      nameOfDays: isOperationalDays
        ? operationalDaysValue
        : nameOfOperationalDays,
      dateDuration: {
        startDate: startDate || moment(),
        endDate: endDate || _.get(currentAcademicYear, "endDate"),
      },
    };
  }

  return {
    title: null,
    grades: [],
    routineMode: ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS,
    rotationDays: _.size(operationalDays),
    nameOfDays: operationalDaysValue,
    dateDuration: {
      startDate: moment(),
      endDate: _.get(currentAcademicYear, "endDate"),
    },
  };
};

export const getDefaultRoutineConfiguration = ({
  routineBasicInfo,
  operationalDays,
}) => {
  const {
    id,
    routineMode,
    dayPatternType,
    rotationDays,
    dayPatterns,
    bellSchedules,
    countHolidayAsRotationDay,
  } = routineBasicInfo || {};

  const showAllWeekdays =
    routineMode === ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS;

  const routineDayPatternSequence = _.find(dayPatterns, ({ relation }) => {
    return _.isNil(relation);
  });

  // if start day is not defined then we need to set first rotation day as start day
  const updatedRotationDays = _.map(rotationDays, (data, index) => ({
    label: _.get(data, "label"),
    value: _.get(data, "id"),
    isStartDay: index === 0 ? true : undefined,
  }));

  const routineDayPatternRotationDays = _.map(
    _.get(routineDayPatternSequence, "rotationDays"),
    ({ rotationDay, isStartCycle }) => {
      const { id, label } = rotationDay || {};
      return {
        value: id,
        label,
        isStartDay: isStartCycle,
        oldPatternId: _.get(routineDayPatternSequence, "id"),
      };
    }
  );

  const routineDayPatternRelation = _.filter(dayPatterns, ({ relation }) => {
    return !_.isNil(relation);
  });

  const routineDayPatternWeekdaysRelation = _.map(
    routineDayPatternRelation,
    ({ id, weekdays, relation, rotationDays }) => {
      const isRelationCycles = relation === "CYCLES";
      const firstRotationDay = _.head(rotationDays);

      const ruleWeekDays = _.map(weekdays, day => DAYS_IN_ORDER[day]);
      const ruleRotationDay = !isRelationCycles
        ? {
            value: _.get(firstRotationDay, "rotationDay.id"),
            label: _.get(firstRotationDay, "rotationDay.label"),
          }
        : null;
      const ruleRotationCycle = isRelationCycles
        ? _.map(rotationDays, ({ rotationDay, isStartCycle }) => {
            const { id, label } = rotationDay;
            return {
              value: id,
              key: generateRandomId(),
              label,
              isStartDay: isStartCycle,
            };
          })
        : [];

      return {
        id,
        ruleWeekDays,
        ruleRelation: relation,
        ruleRotationDay,
        ruleRotationCycle,
      };
    }
  );

  const routineDayScheduleMapping = _.map(
    bellSchedules,
    ({ id, weekday, bellSchedule, rotationDay }) => {
      const weekdayName = DAYS_IN_ORDER[weekday];

      return {
        [id]: {
          bellSchedule: {
            id: _.get(bellSchedule, "id"),
            label: _.get(bellSchedule, "label"),
          },
          rotationDay: {
            id: !_.isNil(weekday) ? weekdayName : _.get(rotationDay, "id"),
            label: !_.isNil(weekday)
              ? weekdayName
              : _.get(rotationDay, "label"),
          },
          dayType: showAllWeekdays ? "OPERATIONAL_DAY" : "ROUTINE_DAY",
          weekday: weekday
            ? {
                id: weekdayName,
                label: weekdayName,
                weekdayNumber: weekday,
              }
            : undefined,
        },
      };
    }
  );

  const dayScheduleMapping = showAllWeekdays
    ? getFormattedWeekdaysBellScheduleMapping({
        operationalDays,
        bellSchedules,
      })
    : routineDayScheduleMapping;

  return {
    routineId: id,
    routineMode,
    dayPattern: dayPatternType ? dayPatternType : "INDEPENDENT_OF_WEEKDAYS",
    countAsHoliday: countHolidayAsRotationDay,
    dayPatternSequence: routineDayPatternSequence
      ? routineDayPatternRotationDays
      : updatedRotationDays,
    oldDayPatternSequence: routineDayPatternSequence
      ? routineDayPatternRotationDays
      : updatedRotationDays,
    dayScheduleMapping,
    oldDayScheduleMapping: dayScheduleMapping,
    weekdaysRelations: routineDayPatternWeekdaysRelation,
    oldWeekdaysRelations: routineDayPatternWeekdaysRelation,
  };
};

export const prepareRoutineBasicDetailsUpdateMutationData = ({
  updatedData,
  orgData,
}) => {
  const isDataSame = compareRoutineOrgAndUpdatedData({ orgData, updatedData });
  if (isDataSame) {
    return {
      isDataSame: true,
      modifiedData: [],
    };
  }

  const addedGrade = _.difference(
    _.get(updatedData, "grades", []),
    _.get(orgData, "grades", [])
  );
  const removedGrade = _.difference(
    _.get(orgData, "grades", []),
    _.get(updatedData, "grades", [])
  );

  const addedDays = _.differenceWith(
    _.get(updatedData, "nameOfDays", {}),
    _.get(orgData, "nameOfDays", {}),
    (data1, data2) => data1.value === data2.value
  );
  const addedDayLabel = _.map(addedDays, day => day.label);

  const removedDays = _.differenceWith(
    _.get(orgData, "nameOfDays", {}),
    _.get(updatedData, "nameOfDays", {}),
    (data1, data2) => data1.value === data2.value
  );
  const removedDaysIds = _.map(removedDays, day => day.value);

  //if label and value same and not included ion removed array then only we can say that those days are updated
  const updatedDays = _.differenceWith(
    _.get(updatedData, "nameOfDays", {}),
    _.get(orgData, "nameOfDays", {}),
    (data1, data2) =>
      (data1.value === data2.value &&
        data1.label === data2.label &&
        data1.abbreviation === data2.abbreviation) ||
      _.includes(addedDayLabel, data1.label)
  );

  //if routine mode has been changed then
  const oldRotationMode = _.get(orgData, "routineMode");
  const newRoutineMode = _.get(updatedData, "routineMode");
  const isRoutineModeChange = !_.isEqual(oldRotationMode, newRoutineMode);

  return {
    isDataSame: false,
    modifiedData: {
      ...updatedData,
      addedGrade,
      removedGrade,
      addedDays: _.map(addedDays, day => ({
        label: day.label,
        abbreviation: _.get(day, "abbreviation"),
      })),
      removedDays: isRoutineModeChange ? [] : removedDaysIds,
      updatedDays: isRoutineModeChange
        ? []
        : _.map(updatedDays, day => ({
            id: day.value,
            label: day.label,
            abbreviation: _.get(day, "abbreviation"),
          })),
    },
  };
};

const compareRoutineOrgAndUpdatedData = ({ orgData, updatedData }) => {
  const fieldToCompare = [
    "label",
    "grades",
    "dateDuration",
    "nameOfDays",
    "rotationDays",
    "routineMode",
  ];

  return _.reduce(
    fieldToCompare,
    (result, field) => {
      switch (field) {
        case "label":
        case "rotationDays":
        case "routineMode":
          result &= _.isEqual(_.get(orgData, field), _.get(updatedData, field));

          break;
        case "grades":
          {
            const orgGrades = _.get(orgData, "grades", []);
            const updatedGrades = _.get(updatedData, "grades", []);

            result &= _.isEqual(_.sortBy(orgGrades), _.sortBy(updatedGrades));
          }
          break;
        case "dateDuration":
          {
            result &= _.isEqual(
              _.get(orgData, "dateDuration.startDate"),
              _.get(updatedData, "dateDuration.startDate")
            );

            result &= _.isEqual(
              _.get(orgData, "dateDuration.endDate"),
              _.get(updatedData, "dateDuration.endDate")
            );
          }
          break;
        case "nameOfDays": {
          const orgNameOfDays = _.map(
            _.get(orgData, "nameOfDays", []),
            data => ({ label: data.label, abbreviation: data.abbreviation })
          );
          const updatedDays = _.map(
            _.get(updatedData, "nameOfDays", []),
            data => ({ label: data.label, abbreviation: data.abbreviation })
          );

          const customComparator = (oldDay, newDay) =>
            _.get(oldDay, "label") === _.get(newDay, "label") &&
            _.get(oldDay, "abbreviation") === _.get(newDay, "abbreviation");

          result &= _.isEqual(
            _.sortBy(orgNameOfDays),
            _.sortBy(updatedDays),
            customComparator
          );
        }
      }

      return result;
    },
    true
  );
};

export const getRoutineCreationSteps = ({ t }) => {
  return [
    {
      label: "General information",
      value: GENERAL_INFORMATION,
    },
    {
      label: "Mapping",
      value: MAPPING,
    },
  ];
};

//this function return new state to be updated based on user input given in basic details screen
export const getNewStateOnRoutineBasicDetailsUpdate = (
  value,
  field,
  otherInfo
) => {
  const params = _.get(value, "params", {});
  switch (field) {
    case "rotationDays": {
      const operationalDays = _.size(_.get(otherInfo, "operationalDays", []));
      const rotationDaysValue = params[field];
      const routineMode = _.get(otherInfo, "basicDetails.routineMode", "");

      const pattern =
        operationalDays != rotationDaysValue
          ? ATTENDANCE_GLOBAL_CONSTANTS.ROTATION_CYCLE
          : ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS;

      const nameParams = getDayNameByPattern({
        pattern,
        basicDetails: otherInfo.basicDetails,
        selectedDays: rotationDaysValue,
        mode: otherInfo.mode,
        operationalDays: otherInfo.operationalDays,
        isModeChanged: !_.isEqual(routineMode, pattern),
      });

      return {
        ...params,
        routineMode: nameParams.pattern,
        nameOfDays: nameParams.days,
      };
    }
    default:
      return params;
  }
};

export const getBellScheduleDaysOptions = ({
  rotationDays,
  operationalDays,
  dayPattern,
  dayScheduleMapping,
}) => {
  const alreadySelectedDayIds = _.reduce(
    dayScheduleMapping,
    (result, mapping) => {
      const mappingValue = _.head(_.values(mapping));
      if (!_.isNil(_.get(mappingValue, "rotationDay"))) {
        return [...result, _.get(mappingValue, "rotationDay.id")];
      } else {
        return result;
      }
    },
    []
  );

  const rotationDaysOptions = _.map(rotationDays, rotationDay => {
    const { id, label } = rotationDay;
    const formattedLabel = convertStringToTestId(label);
    return {
      key: id,
      label,
      disabled: _.includes(alreadySelectedDayIds, id),
      customTestId: `timetable-routine-defineBellScheduleTable-rotationDay-select-item-${formattedLabel}`,
    };
  });
  const dividerOption = { key: "DIVIDER", isDivider: true };
  const operationalDaysOptions = _.map(operationalDays, operationalDay => {
    const weekday = DAYS_IN_ORDER[operationalDay];
    const label = moment(weekday, "ddd").format("dddd");
    const formattedLabel = convertStringToTestId(label);
    return {
      key: weekday,
      label,
      disabled: _.includes(alreadySelectedDayIds, weekday),
      customTestId: `timetable-routine-defineBellScheduleTable-rotationDay-select-item-${formattedLabel}`,
    };
  });
  return dayPattern === "DEPENDENT_OF_WEEKDAYS"
    ? [...rotationDaysOptions, dividerOption, ...operationalDaysOptions]
    : rotationDaysOptions;
};

export const getRoutineRuleWeekdaysOptions = ({
  operationalDays,
  selectedWeekdays,
  ruleWeekDays,
}) => {
  return _.map(operationalDays, operationalDay => {
    const weekday = DAYS_IN_ORDER[operationalDay];
    const isWeekdayAlreadySelected = _.includes(selectedWeekdays, weekday);
    const isWeekdaySelectedInRule = _.includes(ruleWeekDays, weekday);

    const isWeekdayEnabled =
      (isWeekdayAlreadySelected && isWeekdaySelectedInRule) ||
      (!isWeekdayAlreadySelected && !isWeekdaySelectedInRule);

    const formattedLabel = convertStringToTestId(weekday);

    return {
      value: weekday,
      label: weekday,
      customTestId: `timetable-routine-weekdays-dropdown-checklist-item-${formattedLabel}-checkbox`,
      disabled: !isWeekdayEnabled,
    };
  });
};

export const getRoutineRuleRelationOptions = t => {
  return [
    {
      key: "IS",
      label: t("attendance:is_equal_to"),
      subText: t(
        "attendance:selected_weekday_mapping_to_selected_rotation_day"
      ),
    },
    {
      key: "CYCLES",
      label: t("attendance:cycles_between"),
      subText: t("attendance:selected_rotation_day_cycle_to_selected_weekday"),
    },
  ];
};

export const getRoutineRuleDaysOptions = ({ rotationDays }) => {
  return _.map(rotationDays, ({ id, label }) => {
    const formattedLabel = convertStringToTestId(label);
    return {
      value: id,
      label,
      customTestId: `timetable-routine-rotationDay-dropdown-select-item-${formattedLabel}`,
    };
  });
};

const getDayPatternsInputForMutation = ({
  dayPattern,
  dayPatternSequence,
  oldDayPatternSequence,
  weekdaysRelations,
  oldWeekdaysRelations,
}) => {
  switch (dayPattern) {
    case "INDEPENDENT_OF_WEEKDAYS": {
      const rotationDays = _.map(
        dayPatternSequence,
        ({ value, isStartDay }) => {
          return {
            rotationDayId: value,
            isStartCycle: isStartDay ? isStartDay : undefined,
          };
        }
      );

      // if user didn't select any start cycle then by default first day will be start cycle
      if (
        !_.isEmpty(dayPatternSequence) &&
        _.every(rotationDays, day => _.isNil(_.get(day, "isStartCycle")))
      ) {
        _.set(rotationDays, "[0].isStartCycle", true);
      }

      const removed = _.map(
        _.filter(weekdaysRelations, ({ isNew }) => _.isNil(isNew)),
        "id"
      );

      const isRotationDayCycleUpdated = _.find(
        oldDayPatternSequence,
        ({ oldPatternId }) => oldPatternId
      );

      const updatedRotationDayCycleId = _.get(
        isRotationDayCycleUpdated,
        "oldPatternId"
      );

      const isDayPatternUpdated =
        _.size(oldDayPatternSequence) !== _.size(dayPatternSequence) ||
        !_.every(dayPatternSequence, (newPattern, index) => {
          const oldPattern = oldDayPatternSequence[index];
          return _.isEqual(oldPattern, newPattern);
        });

      return {
        added:
          !updatedRotationDayCycleId && !_.isEmpty(rotationDays)
            ? [{ rotationDays }]
            : [],
        removed,
        updated:
          updatedRotationDayCycleId && isDayPatternUpdated
            ? [
                {
                  id: updatedRotationDayCycleId,
                  rotationDays,
                },
              ]
            : [],
      };
    }
    case "DEPENDENT_OF_WEEKDAYS": {
      const addedWithNil = _.map(
        _.filter(
          weekdaysRelations,
          relation => relation.isNew && !relation.isDeleted
        ),
        ({
          ruleRelation,
          ruleWeekDays,
          ruleRotationDay,
          ruleRotationCycle,
        }) => {
          const weekdays = _.intersection(
            DAYS_IN_ORDER,
            ruleWeekDays
          ).map(day => _.indexOf(DAYS_IN_ORDER, day));

          const rotationDays =
            ruleRelation === "IS"
              ? [
                  {
                    rotationDayId: _.get(ruleRotationDay, "value"),
                  },
                ]
              : _.map(ruleRotationCycle, ({ value, isStartDay }) => {
                  return {
                    rotationDayId: value,
                    isStartCycle: isStartDay ? isStartDay : undefined,
                  };
                });
          return {
            weekdays,
            relation: ruleRelation,
            rotationDays,
          };
        }
      );

      // if weekdays, relation and rotation days selected then only send it in mutation input
      const added = _.filter(
        addedWithNil,
        ({ relation, rotationDays, weekdays }) => {
          if (_.isEmpty(rotationDays) || _.isEmpty(weekdays)) return false;
          const isRotationDaySelected = _.every(
            rotationDays,
            ({ rotationDayId }) => rotationDayId
          );
          return relation && isRotationDaySelected && weekdays;
        }
      );

      const removed = _.map(
        _.filter(
          weekdaysRelations,
          relation => relation.isDeleted && !relation.isNew
        ),
        ({ id }) => id
      );
      const removedIndependentWeekdaysPattern = _.get(
        _.head(dayPatternSequence),
        "oldPatternId"
      );

      const updated = _.map(
        _.filter(weekdaysRelations, relation => {
          if (relation.isNew || relation.isDeleted) {
            return false;
          }
          const oldRelation = _.find(
            oldWeekdaysRelations,
            oldRelation => oldRelation.id == relation.id
          );
          if (oldRelation) {
            return JSON.stringify(oldRelation) !== JSON.stringify(relation);
          }
        }),
        ({
          id,
          ruleRelation,
          ruleWeekDays,
          ruleRotationDay,
          ruleRotationCycle,
        }) => {
          const weekdays = _.intersection(
            DAYS_IN_ORDER,
            ruleWeekDays
          ).map(day => _.indexOf(DAYS_IN_ORDER, day));
          const rotationDays =
            ruleRelation === "IS"
              ? [
                  {
                    rotationDayId: _.get(ruleRotationDay, "value"),
                  },
                ]
              : _.map(ruleRotationCycle, ({ value, isStartDay }) => {
                  return {
                    rotationDayId: value,
                    isStartCycle: isStartDay ? isStartDay : undefined,
                  };
                });

          return {
            id,
            weekdays,
            relation: ruleRelation,
            rotationDays,
          };
        }
      );

      return {
        added,
        removed: removedIndependentWeekdaysPattern
          ? [...removed, removedIndependentWeekdaysPattern]
          : removed,
        updated,
      };
    }
  }
};

const getBellScheduleMapForMutation = ({
  routineMode,
  dayScheduleMapping,
  oldDayScheduleMapping,
}) => {
  const isModified = !_.isEqual(dayScheduleMapping, oldDayScheduleMapping);
  if (!isModified) return [];

  const operationalDayType = routineMode === "OPERATIONAL_DAYS";

  const finalDayScheduleMapping = _.filter(dayScheduleMapping, mapping => {
    return _.isNil(_.get(_.head(_.values(mapping)), "isDeleted"));
  });
  const bellScheduleMap = _.map(finalDayScheduleMapping, mapping => {
    const mappingValue = _.head(_.values(mapping));
    const { weekday, rotationDay, bellSchedule } = mappingValue;
    const { weekdayNumber } = weekday || {};

    const weekdayIndex = _.indexOf(DAYS_IN_ORDER, _.get(rotationDay, "id"));
    const isWeekday = weekdayIndex !== -1;

    if (operationalDayType) {
      return {
        weekday: weekdayNumber,
        bellScheduleId: _.get(bellSchedule, "id"),
      };
    } else {
      return {
        weekday: isWeekday ? weekdayIndex : undefined,
        rotationDayId: !isWeekday ? _.get(rotationDay, "id") : undefined,
        bellScheduleId: _.get(bellSchedule, "id"),
      };
    }
  });
  return bellScheduleMap;
};

export const getRoutineConfigurationMutationData = ({
  routineConfiguration,
}) => {
  const {
    routineId,
    routineMode,
    dayPattern,
    countAsHoliday,
    dayPatternSequence,
    oldDayPatternSequence,
    dayScheduleMapping,
    oldDayScheduleMapping,
    weekdaysRelations,
    oldWeekdaysRelations,
  } = routineConfiguration;

  const dayPatternsInput = getDayPatternsInputForMutation({
    dayPattern,
    dayPatternSequence,
    oldDayPatternSequence,
    weekdaysRelations,
    oldWeekdaysRelations,
  });

  // const bellSchedulesInput = getBellSchedulesForMutation({
  //   routineMode,
  //   dayScheduleMapping,
  //   oldDayScheduleMapping,
  // });

  const bellScheduleMapInput = getBellScheduleMapForMutation({
    routineMode,
    dayScheduleMapping,
    oldDayScheduleMapping,
  });

  return {
    routineId,
    dayPatternType: dayPattern,
    countHolidayAsRotationDay:
      dayPattern === "INDEPENDENT_OF_WEEKDAYS" ? countAsHoliday : undefined,
    dayPatterns: dayPatternsInput,
    // bellSchedules: bellSchedulesInput,
    bellScheduleMap: bellScheduleMapInput,
  };
};
export const getParsedSchoolCalendarDays = ({
  routines,
  isCalendarEventItemsEnabled,
}) => {
  return _.reduce(
    routines,
    (result, routine) => {
      const routineDates = _.get(routine, "routineDates", []);
      const calendarEvents = _.get(routine, "calendarEvents", []);
      const calendarEventItems = _.get(routine, "calendarEventItems", []);
      const routineId = _.get(routine, "id", null);
      const routineMode = _.get(routine, "routineMode");
      const dayPatterns = _.get(routine, "dayPatterns", []);

      //for each routineDate create map of date
      _.forEach(routineDates, routineDate => {
        const { date, eventType } = routineDate;
        const dataToBePushed = {
          ...routineDate,
          routineId,
          routineMode,
          eventType,
          dayPatterns,
        };
        const currentDateData = result[date];
        if (currentDateData) {
          const dayData = _.get(currentDateData, "dayData", []);
          dayData.push(dataToBePushed);
        } else {
          result[date] = {
            dayData: [dataToBePushed],
            holidayData: [],
          };
        }
      });

      const formattedCalendarEvents = isCalendarEventItemsEnabled
        ? calendarEventItems
        : calendarEvents;

      //same do for holiday events and add that in date's holidayData
      _.forEach(formattedCalendarEvents, calendarEvent => {
        const { startDate, endDate, id } = calendarEvent;
        const datesArr = getDateArrBetweenDates({
          start: startDate,
          end: endDate,
        });

        //logic :
        //for each event date loop through start date and end date if event is not already present in parsed format then only add event in final result
        _.forEach(datesArr, date => {
          const { eventDate } = date;
          const formateDate = moment(eventDate).format("YYYY-MM-DD");
          const currentDateData = result[formateDate];
          const eventToBePushed = {
            ...calendarEvent,
            routineId,
            // routineLabel,
            // routineRotationDays,
          };

          if (currentDateData) {
            const eventData = _.get(currentDateData, "holidayData", []);
            const isCurrentEventAlreadyAdded = _.find(
              eventData,
              data => data.id == id
            );

            if (!isCurrentEventAlreadyAdded) {
              eventData.push(eventToBePushed);
            }
          } else {
            result[formateDate] = {
              dayData: [],
              holidayData: [eventToBePushed],
            };
          }
        });
      });

      return result;
    },
    {}
  );
};

export const getParsedSchoolCalendarDaysMemoized = _.memoize(
  params => getParsedSchoolCalendarDays(params),
  params => JSON.stringify(params)
);

export const getToddleTimeTableDownloadButtonOptions = ({ routines, t }) => {
  return [
    {
      isItemGroup: true,
      label: t("attendance:select_a_routine"),
      key: "select_a_routine",
      options: _.map(routines, data => ({ label: data.label, key: data.id })),
    },
  ];
};

export const getRoutineFieldComponentOptions = ({ field, params }) => {
  switch (field) {
    case "dateDuration": {
      const startDate = _.get(params, "currentAcademicYear.startDate", "");
      const endDate = _.get(params, "currentAcademicYear.endDate", "");

      return {
        minDate: startDate,
        maxDate: endDate,
      };
    }
    case "grades": {
      const options = _.get(params, "options", []);
      return _.map(options, option => {
        const label = _.get(option, "label", "");
        const formattedLabel = convertStringToTestId(label);
        return {
          ...option,
          customTestId: `timetable-routines-routineModal-grades-select-item-${formattedLabel}`,
        };
      });
    }
    default:
      return [];
  }
};

export const getEntityMappingsSteps = ({ t, showTeachersMapping }) => {
  return [
    ...(showTeachersMapping
      ? [
          {
            label: t("attendance:teacher_email_id"),
            value: "TEACHERS",
          },
        ]
      : []),
    {
      label: t("scheduler:period_plural"),
      value: "PERIODS",
    },
    {
      label: t("common:class_plural"),
      value: "CLASSES",
    },
  ];
};

export const getEdvalEntityMappingsSteps = ({
  edvalDataMappingModalSteps,
  isDailyOrganiser,
}) => {
  return isDailyOrganiser
    ? [
        {
          value: "DATA_FIELDS",
          label: "Data Fields",
        },
      ]
    : _.map(edvalDataMappingModalSteps, ({ label, value }) => ({
        label: label,
        value,
      }));
};

export const getAscToddleTeacherFuzzyMap = ({ teacherMap }) => {
  return _.reduce(
    teacherMap,
    (result, data) => {
      const toolTeacher = _.get(data, "toolTeacher", {});
      const toddleTeacher = _.get(data, "toddleTeacher", {});
      if (!_.isNil(toolTeacher) && !_.isNil(toddleTeacher)) {
        const { id } = toolTeacher;
        result[id] = toddleTeacher.id;
      }
      return result;
    },
    {}
  );
};

export const getAscToddleTeacherMapMemoized = _.memoize(
  params => getAscToddleTeacherFuzzyMap(params),
  params => JSON.stringify(params)
);

export const getAscToddlePeriodFuzzyMap = ({ fuzzyMapPeriod }) => {
  const toddlePeriodsUsed = [];
  return _.reduce(
    fuzzyMapPeriod,
    (result, data) => {
      const toolPeriod = _.get(data, "toolPeriod", {});
      const toddlePeriod = _.get(data, "toddlePeriod", {});
      if (!_.isNil(toolPeriod) && !_.isNil(toddlePeriod)) {
        const { id: toolPeriodId } = toolPeriod;
        const { id: toddlePeriodId } = toddlePeriod;

        if (!_.includes(toddlePeriodsUsed, toddlePeriodId)) {
          result[toolPeriodId] = toddlePeriodId;
          toddlePeriodsUsed.push(toddlePeriodId);
        }
      }
      return result;
    },
    {}
  );
};

export const getAscToddlePeriodFuzzyMapMemoized = _.memoize(
  params => getAscToddlePeriodFuzzyMap(params),
  params => JSON.stringify(params)
);

export const getAscPeriodsMappingCustomValidation = ({
  selectedPeriods,
  value,
}) => {
  const countMap = _.countBy(selectedPeriods);
  return _.get(countMap, value, 0) > 1 ? "Already selected" : "";
};

export const getParsedToddlePeriodOptions = ({
  routinePeriodsOptions,
  periodsDataMapping,
}) => {
  const periodsMap = _.reduce(
    periodsDataMapping,
    (result, value) => {
      if (value !== null) {
        result[value] = true;
      }
      return result;
    },
    {}
  );

  return _.map(routinePeriodsOptions, option => ({
    ...option,
    isDisabled: !!periodsMap[option.value],
  }));
};

export const getServiceClassesDropdownOptions = ({ toolClasses, service }) => {
  switch (service) {
    case "EDVAL":
      return _.map(toolClasses, toolClass => ({
        value: _.snakeCase(toolClass),
        label: toolClass,
      }));
    case "TIMETABLING_SOLUTIONS":
      return _.map(toolClasses, ({ id, name }) => ({
        value: id,
        label: name,
      }));
  }
};

export const getParsedToddleClassesOptions = ({
  toddleClassesOptions,
  classesDataMapping,
}) => {
  const classMap = _.keyBy(classesDataMapping, "toddleClass.id");

  return toddleClassesOptions.map(option => {
    const isDisabled = _.isNil(classMap[_.get(option, "value")])
      ? false
      : _.get(classMap[_.get(option, "value")], "isDeleted")
      ? false
      : !!classMap[_.get(option, "value")];

    return {
      ...option,
      isDisabled,
    };
  });
};

export const getServiceClassesMapping = ({
  classesData,
  service = "EDVAL",
}) => {
  const { mappings: classMappings } = classesData || {};
  return _.map(classMappings, classData => {
    const { toolClass, toddleClass } = classData;

    switch (service) {
      case "EDVAL":
        return {
          id: generateRandomId(),
          grade: _.get(toddleClass, "primaryGrade.id", null),
          toolClass: {
            id: _.snakeCase(toolClass),
            title: toolClass,
          },
          toddleClass,
        };
      case "TIMETABLING_SOLUTIONS":
        return {
          id: generateRandomId(),
          grade: _.get(toddleClass, "primaryGrade.id", null),
          toolClass: {
            id: _.get(toolClass, "id"),
            title: _.get(toolClass, "name"),
          },
          toddleClass,
        };
    }
  });
};

export const getParsedToddleTeachersOptions = ({
  teacherSelectionOptions,
  teacherDataMappings,
}) => {
  const teachersMap = _.reduce(
    teacherDataMappings,
    (result, value) => {
      const toddleTeacherId = _.get(value, "toddleTeacher.id");
      if (toddleTeacherId) {
        result[toddleTeacherId] = true;
      }
      return result;
    },
    {}
  );

  return _.map(teacherSelectionOptions, option => ({
    ...option,
    isDisabled: !!teachersMap[_.get(option, "value")],
  }));
};

export const getParsedToddleSpacesOptions = ({
  spacesSelectionOptions,
  spacesDataMappings,
}) => {
  const spacesMap = _.reduce(
    spacesDataMappings,
    (result, value) => {
      const toddleLocationId = _.get(value, "toddleLocation.id");
      if (toddleLocationId) {
        result[toddleLocationId] = true;
      }
      return result;
    },
    {}
  );

  return _.map(spacesSelectionOptions, option => ({
    ...option,
    isDisabled: !!spacesMap[_.get(option, "value")],
  }));
};

export const getDefaultClassesMapping = ({ classesData, subjectOptions }) => {
  const { toolClasses } = classesData || {};
  return _.map(_.get(classesData, "fuzzyMap"), classData => {
    const { toolClass, toolGroup, toolSubjects } = classData;
    const formattedToolSubjects = _.isNil(toolSubjects)
      ? subjectOptions
      : toolSubjects;
    const findToolClass = _.find(
      toolClasses,
      toolClassData => _.get(toolClassData, "id") === _.get(toolClass, "id")
    );

    return {
      id: generateRandomId(),
      grade: _.get(classData, "toddleClass.primaryGrade.id"),
      ...classData,
      toolGroup,
      toolClassGroupOptions: _.map(
        _.get(findToolClass, "groups"),
        ({ id, name, entireClass }) => ({
          value: id,
          label: name,
          entireClass,
        })
      ),
      toolSubject: _.map(formattedToolSubjects, ({ id, name }) => ({
        value: id,
        label: name,
      })),
      isAllSubjects: _.isNil(toolSubjects),
    };
  });
};

export const parseAscMappingForMutation = ({ dataMapping }) => {
  const teachersMapping = _.get(dataMapping, "TEACHERS", []);
  const periodsMapping = _.get(dataMapping, "PERIODS", {});
  const classMappings = _.get(dataMapping, "CLASSES", []);
  const filteredClassesMapping = _.filter(
    classMappings,
    ({ isDeleted }) => !isDeleted
  );
  const mutationObj = {};

  mutationObj["teachersMapping"] = _.map(
    _.forOwn(teachersMapping),
    (value, key) => {
      return {
        toddleTeacherId: value,
        toolTeacherId: key,
      };
    }
  );

  mutationObj["periodsMapping"] = _.transform(
    periodsMapping,
    (result, value, key) => {
      if (!_.isNil(value)) {
        result.push({
          toddlePeriodId: value,
          toolPeriodId: key,
        });
      }
    },
    []
  );

  mutationObj["classMapping"] = _.map(filteredClassesMapping, data => {
    const {
      toddleClass,
      toolClass,
      toolGroup,
      toolSubject,
      isAllSubjects,
    } = data;
    return {
      toddleClassId: _.get(toddleClass, "id"),
      toolClassId: _.get(toolClass, "id"),
      toolSubjectId: isAllSubjects ? null : _.map(toolSubject, "value"),
      toolGroupId: _.get(toolGroup, "id"),
    };
  });

  return mutationObj;
};

export const parseEdvalMappingForMutation = ({
  dataMapping,
  isEdvalUpload,
}) => {
  const teachersMapping = _.get(dataMapping, "TEACHERS", []);
  const periodsMapping = _.get(dataMapping, "PERIODS", {});
  const classMappings = _.get(dataMapping, "CLASSES", []);
  const filteredClassesMapping = _.filter(
    classMappings,
    ({ isDeleted }) => !isDeleted
  );
  const spaceMappings = _.get(dataMapping, "SPACES", []);
  const dayMappings = _.get(dataMapping, "DAYS", []);
  const yardDutyMappings = _.get(dataMapping, "YARD_DUTIES", []);
  const yardDutyPeriodTimings = _.get(dataMapping, "DUTY_SETTINGS", []);

  const filteredYardDutyPeriodTimings = _.filter(
    yardDutyPeriodTimings,
    ({ startTime, endTime }) => !_.isEmpty(startTime) && !_.isEmpty(endTime)
  );

  const mutationObj = {};

  mutationObj["teachersMapping"] = _.chain(teachersMapping)
    .map(({ toolTeacher, toddleTeacher }) => ({
      toolTeacherId: isEdvalUpload ? toolTeacher : _.get(toolTeacher, "id"),
      toddleTeacherId: _.get(toddleTeacher, "id"),
    }))
    .filter(
      ({ toolTeacherId, toddleTeacherId }) => toolTeacherId && toddleTeacherId
    )
    .value();

  mutationObj["periodsMapping"] = _.chain(periodsMapping)
    .map((value, key) => ({
      toddlePeriodId: value,
      toolPeriodId: key,
    }))
    .filter(
      ({ toddlePeriodId, toolPeriodId }) => toddlePeriodId && toolPeriodId
    )
    .value();

  mutationObj["classMapping"] = _.chain(filteredClassesMapping)
    .map(value => ({
      toddleClassId: _.get(value, "toddleClass.id"),
      toolClassId: isEdvalUpload
        ? _.get(value, "toolClass.title")
        : _.get(value, "toolClass.id"),
    }))
    .filter(({ toddleClassId, toolClassId }) => toddleClassId && toolClassId)
    .value();

  mutationObj["locationMapping"] = _.chain(spaceMappings)
    .map(({ toolLocation, toddleLocation }) => ({
      toolLocationId: isEdvalUpload ? toolLocation : _.get(toolLocation, "id"),
      toddleLocationId: _.get(toddleLocation, "id"),
    }))
    .filter(
      ({ toolLocationId, toddleLocationId }) =>
        toolLocationId && toddleLocationId
    )
    .value();

  mutationObj["dayMapping"] = _.chain(dayMappings)
    .map((value, key) => ({
      toddleDayId: _.get(value, "value", null),
      toolDayId: key,
    }))
    .filter(({ toddleDayId, toolDayId }) => toddleDayId && toolDayId)
    .value();

  mutationObj["yardDutyMapping"] = _.chain(yardDutyMappings)
    .map((value, key) => ({
      toddleDutyId: _.get(value, "value", null),
      toolDutyId: key,
    }))
    .filter(({ toddleDutyId, toolDutyId }) => toddleDutyId && toolDutyId)
    .value();

  if (isEdvalUpload) {
    mutationObj["yardDutyPeriodTimings"] = _.map(
      yardDutyPeriodTimings,
      ({ toolYardDuty, toolDay, toolPeriod, startTime, endTime }) => {
        return {
          toolYardDutyId: toolYardDuty,
          toolDayId: toolDay,
          toolPeriodId: toolPeriod,
          startTime,
          endTime,
        };
      }
    );
  }

  return mutationObj;
};

export const getRoutineRotationDaysHashMapMemoize = _.memoize(
  params => getRoutineRotationDaysHashMap(params),
  params => JSON.stringify(params)
);

const getRoutineRotationDaysHashMap = ({ routineMode, routineDates }) => {
  return _.reduce(
    routineDates,
    (rotationDaysHashMap, rotationDayData) => {
      const date = _.get(rotationDayData, "date");
      const rotationDayNumber =
        routineMode === "ROTATION_CYCLE"
          ? _.get(rotationDayData, "rotationDay")
          : undefined;

      const bellSchedule = _.get(rotationDayData, "bellSchedule", {});

      rotationDaysHashMap[`${date}`] = {
        ...(rotationDayNumber || {}),
        bellSchedule,
      };

      return rotationDaysHashMap;
    },
    {}
  );
};

export const getColumnWidth = ({ containerWidth, noOfColumns }) => {
  const availableTableWidth =
    containerWidth - GUTTER_COLUMN_WIDTH - GRID_COLUMN_GAP * (noOfColumns - 1);

  if (availableTableWidth) {
    const columnWidth =
      (availableTableWidth - PERIODS_COLUMN_WIDTH) / noOfColumns;
    if (columnWidth < MIN_COLUMN_WIDTH) {
      return `${MIN_COLUMN_WIDTH}px`;
    } else if (columnWidth > MAX_COLUMN_WIDTH) {
      return `${MAX_COLUMN_WIDTH}px`;
    } else {
      return `${columnWidth}px`;
    }
  }
};

export const getMaxNumberOfPeriods = ({ columnHeaders }) => {
  let maxPeriods = 0;

  // Iterate over each column to find the maximum number of periods
  _.forEach(columnHeaders, ({ columnPeriods }) => {
    const periodsCount = _.size(columnPeriods);
    if (periodsCount > maxPeriods) {
      maxPeriods = periodsCount;
    }
  });

  return maxPeriods;
};

export const getDummyRowHeaders = ({ maxNoOfPeriods }) => {
  const rowHeaders = [];

  for (let i = 1; i <= maxNoOfPeriods; i++) {
    rowHeaders.push({
      id: `PERIOD_${i}`,
      label: i,
    });
  }

  return rowHeaders;
};

export const createLocalSelections = options => {
  return _.map(options, option => ({
    ..._.pick(option, [
      "period",
      "location",
      "isSelected",
      "teachers",
      "day",
      "rotationDay",
      "subject",
      "startTime",
      "endTime",
      "course",
      "locationV2",
    ]),
    isDirty: false,
  }));
};

const createOriginalSelectionObj = ({
  selectedPeriods,
  periodId,
  isRotationCycle,
  rotationDayId,
  day,
}) => {
  const origSelectionObj = _.find(
    selectedPeriods,
    selection =>
      selection.period.id === periodId &&
      (isRotationCycle
        ? selection.rotationDay.id === rotationDayId
        : selection.day === day)
  );
  return {
    ..._.pick(origSelectionObj, [
      "period",
      "location",
      "isSelected",
      "teachers",
      "day",
      "rotationDay",
      "subject",
      "startTime",
      "endTime",
      "course",
    ]),
    isDirty: false,
  };
};

export const createOrigSelectionObjMemoized = _.memoize(
  params => createOriginalSelectionObj(params),
  params => JSON.stringify(params)
);

const periodsBlockColors = [
  "var(--decorative-background-subtle-violet)",
  "var(--decorative-background-subtle-blue)",
  "var(--decorative-background-subtle-teal)",
  "var(--decorative-background-subtle-pink)",
  "var(--decorative-background-subtle-green)",
  "var(--decorative-background-subtle-purple)",
  "var(--decorative-background-subtle-orange)",
];

const getPeriodSlotColor = ({ periodId }) => {
  let id = periodId;
  if (typeof periodId !== "string") {
    id = periodId?.toString();
  }
  return periodsBlockColors[id?.slice(-4) % _.size(periodsBlockColors)];
};

export const getPeriodSlotColorMemoized = _.memoize(
  params => getPeriodSlotColor(params),
  params => JSON.stringify(params)
);

export const getDropdownTextTruncated = ({
  text,
  length = DEFAULT_TEXT_LENGTH,
  separator = DEFAULT_SEPERATOR,
}) => {
  return _.truncate(text, { length, separator });
};

export const getDropdownTextTruncatedMemoized = _.memoize(
  params => getDropdownTextTruncated(params),
  params => JSON.stringify(params.text)
);

const getBellScheduleIdFromRotationDayId = ({ bellSchedules, targetId }) => {
  const selectedRotationDay = _.find(
    bellSchedules,
    bellSchedule => _.get(bellSchedule, "rotationDay.id") === targetId
  );
  return selectedRotationDay
    ? _.get(selectedRotationDay, "bellSchedule.id")
    : NON_APPLICABLE;
};

export const getBellScheduleIdFromRotationDayIdMemoized = _.memoize(
  getBellScheduleIdFromRotationDayId,
  params => JSON.stringify(params)
);

export const checkUpdatedNodes = ({ routineUpdateNodes, currentRoutines }) => {
  return _.some(routineUpdateNodes, updatedNode => {
    const currentNode = _.find(currentRoutines, [
      "routineId",
      _.get(updatedNode, "routineId"),
    ]);

    // This check is to identify that new selected event type is skip
    const isUpdatedEventTypeIsSkip = _.get(updatedNode, "eventType") === "SKIP";
    // This check is to identify if the current day has already routine update event
    const isEventTypeUpdated =
      !_.isNil(_.get(currentNode, "eventType")) &&
      !_.isNil(_.get(updatedNode, "eventType")) &&
      _.get(currentNode, "eventType") !== _.get(updatedNode, "eventType");

    return (
      currentNode &&
      (_.get(currentNode, "rotationDayId") !==
        _.get(updatedNode, "rotationDayId") ||
        _.get(currentNode, "bellScheduleId") !==
          _.get(updatedNode, "bellScheduleId") ||
        isUpdatedEventTypeIsSkip ||
        isEventTypeUpdated)
    );
  });
};

export const SUBJECT_PRIOTIZED_CURRICULUMS = ["IB_PYP", "UBD"]; // used in multiple utils check before changing it

export const isNonIBCurriculum = curriculum => {
  return _.includes(SUBJECT_PRIOTIZED_CURRICULUMS, curriculum);
};

export const isNonIBCurriculumMemoize = _.memoize(isNonIBCurriculum, params =>
  JSON.stringify(params)
);

const heightRanges = {
  XS: { min: 0, max: SM_HEIGHT_EVENT },
  SM: { min: SM_HEIGHT_EVENT, max: XM_HEIGHT_EVENT },
  MD: { min: XM_HEIGHT_EVENT, max: MD_HEIGHT_EVENT },
  LG: { min: MD_HEIGHT_EVENT, max: Infinity },
};

export const classifyHeight = inputHeight => {
  for (const [key, range] of Object.entries(heightRanges)) {
    if (inputHeight >= range.min && inputHeight <= range.max) {
      return key.toLowerCase();
    }
  }
  return "invalid";
};

export const classifyHeightMemoize = _.memoize(classifyHeight, params =>
  JSON.stringify(params)
);

const isTeacherTimeTableDownloadAllowed = ({
  selectedWeek,
  selectedPeriods,
}) => {
  const { weekStartDate, weekEndDate } = selectedWeek;
  const primaryGradeRoutines = {};
  let isDownloadAllowed = false;

  _.forEach(selectedPeriods, slot => {
    const primaryGradeId = _.get(slot, "course.primaryGrade.id");
    const primaryGrade = _.get(slot, "course.primaryGrade");

    if (!_.get(primaryGradeRoutines, primaryGradeId)) {
      const routines = _.get(primaryGrade, "curriculumProgram.routines", []);
      const routineList = _.filter(routines, routine => {
        const routineEndDate = _.get(routine, "endDate");
        const grades = _.get(routine, "grades");
        if (
          moment(routineEndDate)
            .startOf("day")
            .isSameOrAfter(moment(weekEndDate).startOf("day"))
        ) {
          return _.some(grades, { id: primaryGradeId });
        }
      });
      _.set(primaryGradeRoutines, primaryGradeId, routineList);
    }
  });

  let mergedRoutines = [];

  _.forEach(_.values(primaryGradeRoutines), routines => {
    mergedRoutines = _.concat(mergedRoutines, routines);
  });

  if (_.lte(_.size(mergedRoutines), 1)) {
    isDownloadAllowed = true;
  } else {
    const isAllOperationalDaysRoutines = _.every(mergedRoutines, {
      routineMode: "OPERATIONAL_DAYS",
    });

    if (isAllOperationalDaysRoutines) isDownloadAllowed = true;

    const isAllRotationDaysRoutines = _.every(mergedRoutines, {
      routineMode: "ROTATION_CYCLE",
    });

    if (isAllRotationDaysRoutines) {
      const isDayPatternTypeDistinct =
        _.some(mergedRoutines, { dayPatternType: "INDEPENDENT_OF_WEEKDAYS" }) &&
        _.some(mergedRoutines, { dayPatternType: "DEPENDENT_OF_WEEKDAYS" });
      if (isDayPatternTypeDistinct) {
        isDownloadAllowed = false;
        return;
      } else {
        const isWeekdayDependent = _.every(mergedRoutines, {
          dayPatternType: "DEPENDENT_OF_WEEKDAYS",
        });
        if (isWeekdayDependent) {
          const standardDayPattern = _.get(
            _.first(mergedRoutines),
            "dayPatterns"
          );
          const standardSignatures = new Set(
            _.flatMap(standardDayPattern, _getWeekDayDependentSignatures)
          );
          const allDayPatterns = _.flatMap(mergedRoutines, "dayPatterns");

          isDownloadAllowed = _.every(allDayPatterns, pattern => {
            const signatures = _getWeekDayDependentSignatures(pattern);

            return _.every(signatures, sig => standardSignatures.has(sig));
          });
        } else {
          const standardRotationDays = _.get(
            _.first(mergedRoutines),
            "dayPatterns[0].rotationDays"
          );

          const standardRotationDaysLabel = _.sortBy(
            _.uniq(_.map(standardRotationDays, "rotationDay.label"))
          );

          isDownloadAllowed = _.every(mergedRoutines, routine => {
            const rotationDaysList = _.get(
              routine,
              "dayPatterns[0].rotationDays",
              []
            );

            const rotationDaysLabels = _.sortBy(
              _.uniq(_.map(rotationDaysList, "rotationDay.label"))
            );
            return _.isEqual(rotationDaysLabels, standardRotationDaysLabel);
          });
        }
      }
    }
  }
  return isDownloadAllowed;
};

const _getWeekDayDependentSignatures = pattern => {
  const { weekdays = [], relation, rotationDays = [] } = pattern;

  if (_.isEmpty(weekdays) || _.isEmpty(rotationDays)) {
    return [];
  }

  if (relation === "IS") {
    const rd = _.first(rotationDays);
    const rotationDayLabel = _.get(rd, "rotationDay.label", null);
    return rd ? _.map(weekdays, day => `${day}-${rotationDayLabel}`) : [];
  }

  if (relation === "CYCLES") {
    // Combine weekday with rotation day
    // If size is same then map it one to one
    // If size is different then create all combinations
    const rotationDaySize = _.size(rotationDays);
    const isOneToOneMapping = _.size(weekdays) === rotationDaySize;
    const startCycleIndex = Math.max(
      0,
      _.findIndex(
        rotationDays,
        rd => _.get(rd, "isStartCycleDay", false) == true
      )
    );

    // One-to-one mapping with rotation days
    if (isOneToOneMapping) {
      return _.map(weekdays, (day, index) => {
        const rdIndex = (startCycleIndex + index) % rotationDaySize;
        const rd = _.nth(rotationDays, rdIndex);
        const rotationDayLabel = _.get(rd, "rotationDay.label", null);
        return `${day}-${rotationDayLabel}`;
      });
    }

    return _.flatMap(weekdays, day =>
      _.map(rotationDays, rd => {
        const rotationDayLabel = _.get(rd, "rotationDay.label", null);
        return `${day}-${rotationDayLabel}`;
      })
    );
  }

  return [];
};

export const isTeacherTimeTableDownloadAllowedMemoized = _.memoize(
  isTeacherTimeTableDownloadAllowed,
  params => JSON.stringify(params)
);

export const weekdaysRelationsExists = weekdaysRelations => {
  if (_.size(weekdaysRelations)) {
    for (const weekday of weekdaysRelations) {
      const ruleRelation = _.get(weekday, "ruleRelation");
      const ruleWeekDays = _.get(weekday, "ruleWeekDays");
      const ruleRotationDay = _.get(weekday, "ruleRotationDay");
      const ruleRotationCycle = _.get(weekday, "ruleRotationCycle");

      if (
        ruleRelation &&
        _.size(ruleWeekDays) &&
        (ruleRelation === "IS" ? ruleRotationDay : _.size(ruleRotationCycle))
      ) {
        return true;
      }
    }
    return false;
  } else return false;
};

export const bellScheduleMappingExists = dayScheduleMappings => {
  if (_.size(dayScheduleMappings)) {
    for (const dayScheduleMapping of dayScheduleMappings) {
      const [daySchedule] = _.values(dayScheduleMapping);
      const bellSchedule = _.get(daySchedule, "bellSchedule");
      const rotationDay = _.get(daySchedule, "rotationDay");

      if (bellSchedule && rotationDay) {
        return true;
      }
    }
    return false;
  } else return false;
};

export function checkIsTimeIsSameForPeriod({
  bellSchedulePeriod,
  params,
  updatedSelection,
}) {
  const startTime =
    _.get(params, "startTime") || _.get(updatedSelection, "startTime");
  const isStartTimeSame = moment(
    _.get(bellSchedulePeriod, "startTime"),
    "HH:mm:ss"
  ).isSame(moment(startTime, "hh:mm a"));
  const isEndTimeSame = moment(
    _.get(bellSchedulePeriod, "endTime"),
    "HH:mm:ss"
  ).isSame(moment(_.get(params, "endTime"), "hh:mm a"));

  return !isStartTimeSame || !isEndTimeSame;
}

export const commonFormatTimeComparison = ({ bellSchedulePeriod, slot }) => {
  const format12hr = "hh:mm a";
  const format24hr = "HH:mm:ss";

  const { startTime, endTime } = slot || {};
  const slotStartTime = moment(startTime, format12hr, true).isValid()
    ? moment(startTime, format12hr)
    : moment(startTime, format24hr);
  const slotEndTime = moment(endTime, format12hr, true).isValid()
    ? moment(endTime, format12hr)
    : moment(endTime, format24hr);

  const isStartTimeSame = moment(
    _.get(bellSchedulePeriod, "startTime"),
    "HH:mm:ss"
  ).isSame(slotStartTime);
  const isEndTimeSame = moment(
    _.get(bellSchedulePeriod, "endTime"),
    "HH:mm:ss"
  ).isSame(slotEndTime);

  return !isStartTimeSame || !isEndTimeSame;
};

export const getSortedPeriodList = ({
  enrolledPeriodList,
  periodsOfBellSchedule,
}) => {
  const enrolledPeriodMap = _.keyBy(enrolledPeriodList, "period.id");

  return _.orderBy(periodsOfBellSchedule, [
    ({ period, startTime }) => {
      const periodStartDate = _.get(
        enrolledPeriodMap,
        [_.get(period, "id"), "startTime"],
        startTime
      );

      return moment(periodStartDate, "HH:mm:ss");
    },
  ]);
};

export const getTimetablePeriodSlotType = ({
  teachersV2,
  selectedTeacherId,
}) => {
  const selectedTeacher = _.find(teachersV2, ({ staff }) => {
    const { id: teacherId } = staff;
    return teacherId === selectedTeacherId;
  });
  return _.get(selectedTeacher, "type", "REGULAR");
};

export const getSpacesTableHeaders = ({ t }) => {
  return [
    {
      key: "name",
      value: t("common:name"),
    },
    {
      key: "abbreviation",
      value: t("common:abbreviation"),
      style: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      key: "actions",
      value: "",
      style: {
        width: "40px",
      },
    },
  ];
};

export const getYardDutiesTableHeaders = ({ t }) => {
  return [
    {
      key: "label",
      value: "Label", //localize
    },
    {
      key: "location",
      value: t("common:location"),
      style: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      key: "actions",
      value: "",
      style: {
        width: "40px",
      },
    },
  ];
};

export const getTimetableSlotSpaceOptions = ({ spaces }) => {
  return _.map(spaces, ({ node: { id, label } }) => ({
    value: id,
    label,
  }));
};

export const getFormattedTimeRange = ({
  data,
  selectedWeek,
  currentTimezoneConfig,
  isTimeZoneConversionEnabled,
  getUserTime,
}) => {
  const originalStartTime = `${_.get(data, "date")} ${_.get(
    data,
    "startTime"
  )}`;
  const userStartTime = getUserTime({ time: originalStartTime });
  const formattedUserStartTime = isTimeZoneConversionEnabled
    ? moment(userStartTime).format("HH:mm:ss")
    : _.get(data, "startTime");

  const originalEndTime = `${_.get(data, "date")} ${_.get(data, "endTime")}`;
  const userEndTime = getUserTime({ time: originalEndTime });
  const formattedUserEndTime = isTimeZoneConversionEnabled
    ? moment(userEndTime).format("HH:mm:ss")
    : _.get(data, "endTime");

  const updatedSelectedWeek = moment(_.get(selectedWeek, "weekStartDate"))
    .clone()
    .tz(currentTimezoneConfig, true)
    .toDate();

  return {
    formattedUserStartTime,
    formattedUserEndTime,
    updatedSelectedWeek,
  };
};

export const getColumnTypeLabel = ({ toolType }) => {
  switch (toolType) {
    case "CODE":
      return "Code";
    case "INDEX":
      return "Index";
    case "NAME":
      return "Name";
  }
};

export const getFormattedDataMapping = ({
  periodMapping,
  yardDutyMapping,
  dayMapping,
}) => {
  const filteredPeriodMapping = _.pickBy(periodMapping, (value, key) => {
    return !_.isNil(value) && !_.isNil(key);
  });

  const filteredDayMapping = _.pickBy(dayMapping, (value, key) => {
    return !_.isNil(_.get(value, "value")) && !_.isNil(key);
  });

  const filteredYardDutyMapping = _.pickBy(yardDutyMapping, (value, key) => {
    return !_.isNil(_.get(value, "value")) && !_.isNil(key);
  });

  const periodMappingArray = _.map(filteredPeriodMapping, (value, key) => {
    return {
      toddlePeriodId: value,
      toolPeriodId: key,
    };
  });

  const dayMappingArray = _.map(filteredDayMapping, (value, key) => {
    return {
      toddleDayId: _.get(value, "value"),
      toolDayId: key,
    };
  });

  const yardDutyMappingArray = _.map(filteredYardDutyMapping, (value, key) => {
    return {
      toddleDutyId: _.get(value, "value"),
      toolDutyId: key,
    };
  });

  return {
    periodMapping: periodMappingArray,
    dayMapping: dayMappingArray,
    yardDutyMapping: yardDutyMappingArray,
  };
};

export const getEdvalIntegrationSteps = ({
  t,
  showYardDuties = false,
  showDutyTimings = false,
}) => {
  const edvalIntegrationSteps = [
    { value: "DATA_FIELDS", label: t("attendance:data_fields") },
    { value: "CLASSES", label: t("common:class_plural") },
    { value: "TEACHERS", label: t("common:teacher_plural") },
    { value: "DAYS", label: t("common:days_other") },
    { value: "PERIODS", label: t("common:period_plural") },
    { value: "SPACES", label: t("attendance:locations") },
  ];

  if (showYardDuties) {
    edvalIntegrationSteps.push({
      value: "YARD_DUTIES",
      label: t("common:yard_duty_other"),
    });
    if (showDutyTimings) {
      edvalIntegrationSteps.push({
        value: "DUTY_SETTINGS",
        label: t("attendance:duty_settings"),
      });
    }
  }

  return edvalIntegrationSteps;
};

export const getTTSIntegrationSteps = ({ t }) => {
  const edvalIntegrationSteps = [
    { value: "CLASSES", label: t("common:class_plural") },
    { value: "TEACHERS", label: t("common:teacher_plural") },
    { value: "DAYS", label: t("common:days_other") },
    { value: "PERIODS", label: t("common:period_plural") },
    { value: "SPACES", label: t("attendance:locations") },
    {
      value: "YARD_DUTIES",
      label: t("common:yard_duty_other"),
    },
  ];

  return edvalIntegrationSteps;
};

export const getIsAllSlotFromSubjectPriotizedCurriculum = ({ slots }) => {
  return _.every(slots, ({ course, subject }) => {
    // Get curriculum program type from course
    const curriculumType = _.get(course, "curriculumProgram.type", null);
    // If curriculumType exists, check if it's in the allowed list
    return curriculumType
      ? _.includes(SUBJECT_PRIOTIZED_CURRICULUMS, curriculumType)
      : true;
  });
};

export const getTimetableViewTypeOptions = ({ t }) => {
  return [
    {
      key: "WEEKLY",
      label: t("common:week"),
    },
    {
      key: "SCHEDULE",
      label: t("common:schedule"),
    },
  ];
};

export const getTimetableSlotsGroupedByDates = ({
  selectedWeek,
  yardDuties,
  selectedPeriods,
}) => {
  const weekStartDate = moment(_.get(selectedWeek, "weekStartDate"));
  const weekEndDate = moment(_.get(selectedWeek, "weekEndDate"));

  const allDates = [];
  const currentDate = weekStartDate.clone();
  while (
    currentDate.isBefore(weekEndDate) ||
    currentDate.isSame(weekEndDate, "day")
  ) {
    allDates.push(currentDate.format("YYYY-MM-DD"));
    currentDate.add(1, "day");
  }

  const allSlots = _.sortBy([...selectedPeriods, ...yardDuties], "startTime");
  const groupedByDate = _.groupBy(allSlots, "date");

  const structuredSlots = allDates.map(date => ({
    date,
    slots: _.get(groupedByDate, date, []),
  }));

  return structuredSlots;
};

export const getTimetableWithClassRedirection = ({
  isStudentTimetableClassRedirectionEnabled,
  isRnpConsumptionEnabled,
}) => {
  const timetableWithClassRedirection = [
    {
      timetableType: "STUDENT",
      isValid:
        isStudentTimetableClassRedirectionEnabled && !isRnpConsumptionEnabled,
    },
    { timetableType: "TEACHER", isValid: true },
  ];

  return _.reduce(
    timetableWithClassRedirection,
    (acc, { timetableType, isValid }) => {
      if (isValid) {
        acc.push(timetableType);
      }
      return acc;
    },
    []
  );
};

export const getPreferencesQueryVariables = ({
  teacherCurriculumProgramIds,
}) => {
  return {
    configurationEntityGroupFilters: _.map(
      teacherCurriculumProgramIds,
      curriculumProgramId => {
        return {
          configurationEntityGroups: [
            {
              entityId: curriculumProgramId,
              entityType: "CURRICULUM_PROGRAM",
            },
          ],
          configurations: ["EnableEditAttendanceInPastByTeacher"],
        };
      }
    ),
    isAdminPage: true,
    isSettingsPage: false,
    isCourseModuleSetting: false,
  };
};

export const getPreferencesQueryVariablesMemoized = _.memoize(
  params => getPreferencesQueryVariables(params),
  params => JSON.stringify(params)
);

export const SIDEBAR_KEY_TO_ROUTE_MAP = {
  CHECKLIST: "checklist",
  PERIODS: "periods-setup",
  GENERAL_SETTINGS: "general-settings",
  ROUTINES: "routines",
  CLASS_TIMETABLE: "class-timetable",
  STUDENT_TIMETABLE: "student-timetable",
  TEACHER_TIMETABLE: "teacher-timetable",
  IMPORT_LOG: "import-log",
  BELL_SCHEDULES: "bell-schedule",
  SPACES: "locations",
  YARD_DUTIES: "yard-duties",
  SCHEDULE_CALENDAR: "school-days",
};

export const ROUTE_TO_SIDEBAR_KEY_MAP = {
  checklist: "CHECKLIST",
  "periods-setup": "PERIODS",
  "general-settings": "GENERAL_SETTINGS",
  routines: "ROUTINES",
  "class-timetable": "CLASS_TIMETABLE",
  "student-timetable": "STUDENT_TIMETABLE",
  "teacher-timetable": "TEACHER_TIMETABLE",
  "import-log": "IMPORT_LOG",
  "bell-schedule": "BELL_SCHEDULES",
  locations: "SPACES",
  "yard-duties": "YARD_DUTIES",
  "school-days": "SCHEDULE_CALENDAR",
};

// Helper function to get current route from pathname
export const getCurrentRouteFromPath = pathname => {
  const pathSegments = pathname.split("/");
  const timetableIndex = pathSegments.findIndex(
    segment => segment === "timetable"
  );
  if (timetableIndex !== -1 && timetableIndex < pathSegments.length - 1) {
    return pathSegments[timetableIndex + 1];
  }
  return null;
};
